import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import Map, { 
  Marker,
  Source,
  Layer,
  MapRef,
  MarkerDragEvent,
  MapMouseEvent,
  ViewStateChangeEvent
} from 'react-map-gl/mapbox';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Box, Paper, Typography, IconButton, InputBase } from '@mui/material';
import { ToolMode } from './BottomRightTools';
import type { LatLng, ArrowDescriptor, ArrowDraft, ArrowInput } from '../types/arrows';
import type { DelineatorLine, DelineatorDraft, DelineatorInput, DelineatorDeviceType } from '../types/delineators';
import type { SelectedElement, RoadOverlaySettings } from '../types/selection';
import { DEFAULT_ROAD_OVERLAY_SETTINGS } from '../types/selection';
import type { SignEdgeMetadata, SnapResult } from '../types/signs';
import logo from '../assets/logo.svg';



// Haversine distance calculation (same as original)
const toRadians = (deg: number) => (deg * Math.PI) / 180;
const toDegrees = (rad: number) => (rad * 180) / Math.PI;
const haversineDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const R = 6371000; // meters
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const bearingDegrees = (start: LatLng, end: LatLng) => {
  const phi1 = toRadians(start.lat);
  const phi2 = toRadians(end.lat);
  const deltaLambda = toRadians(end.lng - start.lng);
  const y = Math.sin(deltaLambda) * Math.cos(phi2);
  const x = Math.cos(phi1) * Math.sin(phi2) - Math.sin(phi1) * Math.cos(phi2) * Math.cos(deltaLambda);
  const theta = Math.atan2(y, x);
  return (toDegrees(theta) + 360) % 360;
};

// Offset a point by a bearing and distance (in meters)
const offsetLatLng = (point: LatLng, bearing: number, distanceMeters: number): LatLng => {
  const R = 6371000; // Earth radius in meters
  const delta = distanceMeters / R;
  const theta = toRadians(bearing);
  const phi1 = toRadians(point.lat);
  const lambda1 = toRadians(point.lng);

  const phi2 = Math.asin(
    Math.sin(phi1) * Math.cos(delta) + Math.cos(phi1) * Math.sin(delta) * Math.cos(theta)
  );
  const lambda2 = lambda1 + Math.atan2(
    Math.sin(theta) * Math.sin(delta) * Math.cos(phi1),
    Math.cos(delta) - Math.sin(phi1) * Math.sin(phi2)
  );

  return {
    lat: toDegrees(phi2),
    lng: toDegrees(lambda2),
  };
};

const defaultCenter = {
  lat: -33.8688, // Sydney
  lng: 151.2093,
};

// Calculate device positions along a path at regular spacing intervals
const calculateDelineatorPositions = (
  path: Array<{ lat: number; lng: number }>,
  spacingMeters: number
): Array<{ lat: number; lng: number }> => {
  if (path.length < 2) return [];
  
  const positions: Array<{ lat: number; lng: number }> = [];
  let accumulated = 0;
  
  // Always place a device at the start
  positions.push(path[0]);
  
  for (let i = 0; i < path.length - 1; i++) {
    const p1 = path[i];
    const p2 = path[i + 1];
    const segmentLength = haversineDistance(p1.lat, p1.lng, p2.lat, p2.lng);
    
    // Calculate how far along the segment we need to start
    let remaining = spacingMeters - accumulated;
    
    while (remaining <= segmentLength) {
      // Interpolate position along segment
      const ratio = remaining / segmentLength;
      const lat = p1.lat + (p2.lat - p1.lat) * ratio;
      const lng = p1.lng + (p2.lng - p1.lng) * ratio;
      positions.push({ lat, lng });
      remaining += spacingMeters;
    }
    
    // Update accumulated distance for next segment
    accumulated = segmentLength - (remaining - spacingMeters);
  }
  
  return positions;
};

// Abstract drawing mode type (compatible with both Google and Mapbox)
type DrawingMode = 'polygon' | 'rectangle' | null;

// Props interface - identical to WorkzoneMap for seamless switching
interface WorkzoneMapMapboxProps {
  apiKey: string | undefined;
  onWorkzoneSelect: (data: any) => void;
  overlays?: any | null;
  signPlacement?: any | null;
  cones?: any[] | null;
  signLibrary?: any | null;
  isManualMode?: boolean;
  onSignMove?: (signId: string, newLatLng: { lat: number; lng: number }, edgeMetadata?: SignEdgeMetadata) => void;
  onSignAdd?: (signCode: string, newLatLng: { lat: number; lng: number }, rotation?: number, composite?: any, edgeMetadata?: SignEdgeMetadata) => void;
  onSignRemove?: (signId: string) => void;
  onSignResize?: (signId: string, newScale: number) => void;
  onSignRotate?: (signId: string, newRotation: number) => void;
  onSignCalloutMove?: (signId: string, offset: { x: number; y: number }) => void;
  isDrawingPrintArea?: boolean;
  isDrawingWorkzone?: boolean;
  onPrintAreaSelect?: (bounds: any) => void;
  // Modified onMapReady to use abstract DrawingMode instead of Google-specific type
  onMapReady?: (controls: {
    zoomIn: () => void;
    zoomOut: () => void;
    setDrawingMode: (mode: DrawingMode) => void;
    snapToLocation: (location?: { lat: number; lng: number }) => void;
    getMapInstance: () => any; // Returns mapboxgl.Map instance
    captureCanvas: (boundingBox?: { x: number; y: number; width: number; height: number }, fadeOpacity?: number) => Promise<string | null>; // Capture map as base64 data URL, optionally cropped to bounding box, with optional fade on map only
  }) => void;
  onAreaSelected?: (area: any) => void;
  onWorkzoneDrawingComplete?: () => void;
  currentTool?: ToolMode;
  arrowDraft?: ArrowDraft | null;
  arrows?: ArrowDescriptor[];
  onArrowDraftStart?: (point: LatLng) => void;
  onArrowComplete?: (arrow: ArrowInput) => void;
  onArrowDistanceChange?: (id: string, distance: number) => void;
  onArrowDelete?: (id: string) => void;
  onArrowMove?: (id: string, start: LatLng, end: LatLng) => void;
  // Delineator props
  delineatorDraft?: DelineatorDraft | null;
  delineatorLines?: DelineatorLine[];
  onDelineatorDraftStart?: (deviceType: DelineatorDeviceType) => void;
  onDelineatorDraftAddPoint?: (point: LatLng) => void;
  onDelineatorComplete?: (delineator: DelineatorInput) => void;
  onDelineatorDelete?: (id: string) => void;
  onDelineatorUpdate?: (id: string, updates: Partial<DelineatorLine>) => void;
  // Selection props
  selectedElement?: SelectedElement;
  onElementSelect?: (element: SelectedElement) => void;
  onClearSelection?: () => void;
  workzones?: any[];
  roadToolGeometry?: any | null;
  roadToolVisible?: boolean;
  isProMode?: boolean;
  printArea?: { northeast: { lat: number; lng: number }; southwest: { lat: number; lng: number } } | null;
  paperWidth?: number | null;
  paperHeight?: number | null;
  onPaperSizeChange?: (paperWidth: number, paperHeight: number, sizeName: string) => void;
  onMapFadeChange?: (opacity: number) => void;
  // Road overlay settings
  roadOverlaySettings?: RoadOverlaySettings;
  onRoadOverlaySettingsChange?: (settings: Partial<RoadOverlaySettings>) => void;
  // Loaded worksite location (from project load) - will auto-populate search and fly to location
  loadedWorksiteLocation?: {
    lat: number;
    lng: number;
    address?: string;
  } | null;
}

// Paper size options (matching PrintSettings.tsx)
type PaperSize = 'A4' | 'A3' | 'A2' | 'A1' | 'A0';
type Orientation = 'landscape' | 'portrait';

interface PaperOption {
  size: PaperSize;
  orientation: Orientation;
  width: number;  // mm
  height: number; // mm
  pixelWidth: number;  // at 300 DPI
  pixelHeight: number; // at 300 DPI
  label: string;
}

// Paper sizes in mm and pixels at 300 DPI
const MM_TO_PIXELS_300DPI = 300 / 25.4; // 11.811 pixels per mm

const PAPER_OPTIONS: PaperOption[] = [
  // Landscape options
  { size: 'A4', orientation: 'landscape', width: 297, height: 210, pixelWidth: Math.round(297 * MM_TO_PIXELS_300DPI), pixelHeight: Math.round(210 * MM_TO_PIXELS_300DPI), label: 'A4 Landscape' },
  { size: 'A3', orientation: 'landscape', width: 420, height: 297, pixelWidth: Math.round(420 * MM_TO_PIXELS_300DPI), pixelHeight: Math.round(297 * MM_TO_PIXELS_300DPI), label: 'A3 Landscape' },
  { size: 'A2', orientation: 'landscape', width: 594, height: 420, pixelWidth: Math.round(594 * MM_TO_PIXELS_300DPI), pixelHeight: Math.round(420 * MM_TO_PIXELS_300DPI), label: 'A2 Landscape' },
  { size: 'A1', orientation: 'landscape', width: 841, height: 594, pixelWidth: Math.round(841 * MM_TO_PIXELS_300DPI), pixelHeight: Math.round(594 * MM_TO_PIXELS_300DPI), label: 'A1 Landscape' },
  { size: 'A0', orientation: 'landscape', width: 1189, height: 841, pixelWidth: Math.round(1189 * MM_TO_PIXELS_300DPI), pixelHeight: Math.round(841 * MM_TO_PIXELS_300DPI), label: 'A0 Landscape' },
  // Portrait options
  { size: 'A4', orientation: 'portrait', width: 210, height: 297, pixelWidth: Math.round(210 * MM_TO_PIXELS_300DPI), pixelHeight: Math.round(297 * MM_TO_PIXELS_300DPI), label: 'A4 Portrait' },
  { size: 'A3', orientation: 'portrait', width: 297, height: 420, pixelWidth: Math.round(297 * MM_TO_PIXELS_300DPI), pixelHeight: Math.round(420 * MM_TO_PIXELS_300DPI), label: 'A3 Portrait' },
  { size: 'A2', orientation: 'portrait', width: 420, height: 594, pixelWidth: Math.round(420 * MM_TO_PIXELS_300DPI), pixelHeight: Math.round(594 * MM_TO_PIXELS_300DPI), label: 'A2 Portrait' },
  { size: 'A1', orientation: 'portrait', width: 594, height: 841, pixelWidth: Math.round(594 * MM_TO_PIXELS_300DPI), pixelHeight: Math.round(841 * MM_TO_PIXELS_300DPI), label: 'A1 Portrait' },
  { size: 'A0', orientation: 'portrait', width: 841, height: 1189, pixelWidth: Math.round(841 * MM_TO_PIXELS_300DPI), pixelHeight: Math.round(1189 * MM_TO_PIXELS_300DPI), label: 'A0 Portrait' },
];

export const WorkzoneMapMapbox: React.FC<WorkzoneMapMapboxProps> = ({
  apiKey,
  onWorkzoneSelect,
  overlays,
  signPlacement,
  cones,
  signLibrary,
  isManualMode = false,
  onSignMove,
  onSignAdd,
  onSignRemove,
  onSignResize,
  onSignRotate,
  onSignCalloutMove,
  isDrawingPrintArea = false,
  isDrawingWorkzone = false,
  onPrintAreaSelect,
  onMapReady,
  onAreaSelected,
  onWorkzoneDrawingComplete,
  currentTool = 'cursor',
  arrowDraft = null,
  arrows = [],
  onArrowDraftStart,
  onArrowComplete,
  onArrowDistanceChange,
  onArrowDelete,
  onArrowMove,
  delineatorDraft = null,
  delineatorLines = [],
  onDelineatorDraftStart,
  onDelineatorDraftAddPoint,
  onDelineatorComplete,
  onDelineatorDelete,
  onDelineatorUpdate,
  selectedElement,
  onElementSelect,
  onClearSelection,
  workzones = [],
  roadToolGeometry = null,
  roadToolVisible = false,
  isProMode = false,
  printArea = null,
  paperWidth = null,
  paperHeight = null,
  onPaperSizeChange,
  onMapFadeChange,
  roadOverlaySettings: externalRoadOverlaySettings,
  onRoadOverlaySettingsChange,
  loadedWorksiteLocation = null,
}) => {
  const mapRef = useRef<MapRef>(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [isDraggedOver, setIsDraggedOver] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [selectedSign, setSelectedSign] = useState<any | null>(null);
  const [drawingMode, setDrawingMode] = useState<DrawingMode>(null);
  
  // Paper size menu state
  const [paperSizeMenuAnchor, setPaperSizeMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedPaperLabel, setSelectedPaperLabel] = useState<string>('A3 Landscape');
  const [mapFadeOpacity, setMapFadeOpacity] = useState(0);
  const [showRoadMarkings, setShowRoadMarkings] = useState(false);
  
  // Road overlay settings - use external settings if provided, otherwise use defaults
  const [roadOverlaySettings, setRoadOverlaySettings] = useState<RoadOverlaySettings>(
    externalRoadOverlaySettings || DEFAULT_ROAD_OVERLAY_SETTINGS
  );
  
  // Track road feature IDs that have signs on them (for "show only near signs" filter)
  const [signRoadFeatureIds, setSignRoadFeatureIds] = useState<Set<number>>(new Set());
  
  // Sync with external settings
  useEffect(() => {
    if (externalRoadOverlaySettings) {
      setRoadOverlaySettings(externalRoadOverlaySettings);
    }
  }, [externalRoadOverlaySettings]);
  
  // Track last loaded worksite to avoid re-loading the same location
  const lastLoadedWorksiteRef = useRef<string | null>(null);
  
  // Handle loaded worksite location from project load
  // This populates the search bar and flies to the location
  // Also handles clearing when switching to a new project (null location)
  useEffect(() => {
    // Handle null case (new project or cleared project)
    if (!loadedWorksiteLocation) {
      // Clear the search bar and selected location when starting a new project
      if (lastLoadedWorksiteRef.current !== null) {
        setSearchText('');
        setSelectedLocation(null);
        lastLoadedWorksiteRef.current = null;
      }
      return;
    }
    
    if (!isMapLoaded || !mapRef.current) return;
    
    // Create a key to identify this location
    const locationKey = `${loadedWorksiteLocation.lat},${loadedWorksiteLocation.lng}`;
    
    // Skip if we've already loaded this exact location
    if (lastLoadedWorksiteRef.current === locationKey) return;
    lastLoadedWorksiteRef.current = locationKey;
    
    // Set the search text to the address
    if (loadedWorksiteLocation.address) {
      setSearchText(loadedWorksiteLocation.address);
    }
    
    // Set the selected location
    setSelectedLocation({
      lat: loadedWorksiteLocation.lat,
      lng: loadedWorksiteLocation.lng,
    });
    
    // Fly to the location
    const map = mapRef.current.getMap();
    if (map) {
      map.flyTo({
        center: [loadedWorksiteLocation.lng, loadedWorksiteLocation.lat],
        zoom: 18,
        duration: 1500,
      });
    }
  }, [loadedWorksiteLocation, isMapLoaded]);
  
  // Handle road overlay settings change
  const handleRoadOverlaySettingsChange = useCallback((updates: Partial<RoadOverlaySettings>) => {
    setRoadOverlaySettings(prev => {
      const newSettings = { ...prev, ...updates };
      onRoadOverlaySettingsChange?.(updates);
      return newSettings;
    });
  }, [onRoadOverlaySettingsChange]);
  
  // Callout dragging state
  const [draggingCallout, setDraggingCallout] = useState<{
    signId: string;
    startMouseX: number;
    startMouseY: number;
    startOffsetX: number;
    startOffsetY: number;
    currentOffsetX: number;
    currentOffsetY: number;
  } | null>(null);
  
  // Drawing state for polygon
  const [drawingPoints, setDrawingPoints] = useState<LatLng[]>([]);
  
  // Arrow drawing state
  const arrowDraftRef = useRef<ArrowDraft | null>(arrowDraft);
  const [tempArrowEnd, setTempArrowEnd] = useState<LatLng | null>(null);

  // Delineator drawing state - preview cursor position
  const [tempDelineatorEnd, setTempDelineatorEnd] = useState<LatLng | null>(null);
  
  // Delineator dragging state - for moving completed lines
  const [draggingDelineator, setDraggingDelineator] = useState<{
    lineId: string;
    startLat: number;
    startLng: number;
    originalPath: LatLng[];
  } | null>(null);

  // Arrow dragging state - for moving entire arrows
  const [draggingArrow, setDraggingArrow] = useState<{
    arrowId: string;
    startMouseLat: number;
    startMouseLng: number;
    originalStart: LatLng;
    originalEnd: LatLng;
  } | null>(null);
  
  // Track drag offset for smooth visual updates without re-rendering
  const [arrowDragOffset, setArrowDragOffset] = useState<{ lat: number; lng: number } | null>(null);

  const [viewState, setViewState] = useState({
    longitude: defaultCenter.lng,
    latitude: defaultCenter.lat,
    zoom: 5,
    pitch: 10,
    bearing: 95,
  });

  // Screen-space bounding box overlay state
  const [boundingBoxOverlay, setBoundingBoxOverlay] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const [isDraggingOverlay, setIsDraggingOverlay] = useState(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number; boxX: number; boxY: number } | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);

  // Constants matching backend export_renderer.py
  const DOCUMENT_PADDING = 100;
  const MAP_MARGIN = 120;

  // Calculate bounding box dimensions based on paper size
  const calculateBoundingBoxSize = useCallback((containerWidth: number, containerHeight: number) => {
    // Default to A3 landscape if no paper dimensions provided
    const pWidth = paperWidth || 4961;  // A3 landscape @ 300 DPI
    const pHeight = paperHeight || 3508;
    
    // Calculate the map area aspect ratio (matching backend)
    const mapAreaWidth = pWidth - 2 * DOCUMENT_PADDING;
    const mapAreaHeight = pHeight - 2 * MAP_MARGIN;
    const aspectRatio = mapAreaWidth / mapAreaHeight;
    
    // Size the bounding box to fit nicely on screen (60% of container width)
    const maxWidth = containerWidth * 0.6;
    const maxHeight = containerHeight * 0.7;
    
    let boxWidth: number;
    let boxHeight: number;
    
    // Calculate size maintaining aspect ratio
    if (maxWidth / aspectRatio <= maxHeight) {
      boxWidth = maxWidth;
      boxHeight = maxWidth / aspectRatio;
    } else {
      boxHeight = maxHeight;
      boxWidth = maxHeight * aspectRatio;
    }
    
    return { width: boxWidth, height: boxHeight };
  }, [paperWidth, paperHeight]);

  // Update drawing mode based on props
  // For bounding box (print area), show screen-space overlay instead of drawing on map
  useEffect(() => {
    if (isDrawingPrintArea) {
      // Initialize the bounding box overlay in the center of the screen
      if (mapContainerRef.current) {
        const rect = mapContainerRef.current.getBoundingClientRect();
        const { width: boxWidth, height: boxHeight } = calculateBoundingBoxSize(rect.width, rect.height);
        setBoundingBoxOverlay({
          x: (rect.width - boxWidth) / 2,
          y: (rect.height - boxHeight) / 2,
          width: boxWidth,
          height: boxHeight,
        });
      }
      setDrawingMode('rectangle');
    } else if (isDrawingWorkzone) {
      setDrawingMode('polygon');
    } else {
      // Clear overlay when not drawing print area
      setBoundingBoxOverlay(null);
      setDrawingMode(null);
    }
  }, [isDrawingPrintArea, isDrawingWorkzone, calculateBoundingBoxSize]);

  // Update bounding box size when paper dimensions change (from Properties panel)
  useEffect(() => {
    if (boundingBoxOverlay && isDrawingPrintArea && mapContainerRef.current) {
      const rect = mapContainerRef.current.getBoundingClientRect();
      const { width: boxWidth, height: boxHeight } = calculateBoundingBoxSize(rect.width, rect.height);
      
      // Keep the box centered when resizing
      const currentCenterX = boundingBoxOverlay.x + boundingBoxOverlay.width / 2;
      const currentCenterY = boundingBoxOverlay.y + boundingBoxOverlay.height / 2;
      
      setBoundingBoxOverlay({
        x: currentCenterX - boxWidth / 2,
        y: currentCenterY - boxHeight / 2,
        width: boxWidth,
        height: boxHeight,
      });
    }
  }, [paperWidth, paperHeight]);

  // Sync arrow draft ref
  useEffect(() => {
    arrowDraftRef.current = arrowDraft;
    if (!arrowDraft?.start) {
      setTempArrowEnd(null);
    }
  }, [arrowDraft]);

  // Keyboard handling for delineator tool
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (currentTool !== 'delineator' || !delineatorDraft) return;
      
      // Escape to cancel
      if (e.key === 'Escape') {
        setTempDelineatorEnd(null);
        // Reset to cursor mode - parent will handle clearing draft
        onDelineatorComplete?.({
          deviceType: delineatorDraft.deviceType,
          path: [], // Empty path = cancel
          spacing: delineatorDraft.spacing,
          deviceSize: delineatorDraft.deviceSize,
          geometryType: delineatorDraft.geometryType,
        });
      }
      
      // Enter to complete (if we have at least 2 points)
      if (e.key === 'Enter' && delineatorDraft.path.length >= 2) {
        setTempDelineatorEnd(null);
        onDelineatorComplete?.({
          deviceType: delineatorDraft.deviceType,
          path: delineatorDraft.path,
          spacing: delineatorDraft.spacing,
          deviceSize: delineatorDraft.deviceSize,
          geometryType: delineatorDraft.geometryType,
        });
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentTool, delineatorDraft, onDelineatorComplete]);

  // Track if fade layer has been properly positioned
  const fadeLayerPositionedRef = useRef(false);

  // Apply map fade overlay using imperative Mapbox API
  // This creates a white fill layer that covers the entire map, positioned ABOVE the basemap
  // but BELOW our custom layers (workzones, arrows, delineators)
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    const FADE_LAYER_ID = 'tgs-map-fade-overlay';
    const FADE_SOURCE_ID = 'tgs-map-fade-source';
    let repositionTimeout: NodeJS.Timeout | null = null;

    const ensureFadeLayer = () => {
      if (!map.isStyleLoaded()) return;

      // Add source if it doesn't exist (covers entire world)
      if (!map.getSource(FADE_SOURCE_ID)) {
        map.addSource(FADE_SOURCE_ID, {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'Polygon',
              coordinates: [[
                [-180, -90],
                [180, -90],
                [180, 90],
                [-180, 90],
                [-180, -90],
              ]],
            },
          },
        });
      }

      // Add fade layer if it doesn't exist
      if (!map.getLayer(FADE_LAYER_ID)) {
        map.addLayer({
          id: FADE_LAYER_ID,
          type: 'fill',
          source: FADE_SOURCE_ID,
          paint: {
            'fill-color': '#ffffff',
            'fill-opacity': mapFadeOpacity,
          },
        });
        fadeLayerPositionedRef.current = false; // Need to position it
      } else {
        // Update opacity
        map.setPaintProperty(FADE_LAYER_ID, 'fill-opacity', mapFadeOpacity);
      }
    };

    const positionFadeLayer = () => {
      if (!map.isStyleLoaded() || !map.getLayer(FADE_LAYER_ID)) return;

      // Move fade layer below our custom layers
      // Find the first custom layer and move fade below it
      const customLayerIds = [
        // Road markings (tgs-* layers)
        'tgs-surfaces-base', 'tgs-markings-base',
        'tgs-tunnel-casing', 'tgs-tunnel-surface',
        'tgs-service-casing', 'tgs-service-surface',
        'tgs-street-casing', 'tgs-street-surface', 'tgs-street-centerline', 'tgs-street-lane-divider',
        'tgs-secondary-casing', 'tgs-secondary-surface', 'tgs-secondary-centerline', 'tgs-secondary-lane-divider',
        'tgs-primary-casing', 'tgs-primary-surface', 'tgs-primary-lane-divider-left', 'tgs-primary-lane-divider-right',
        'tgs-trunk-casing', 'tgs-trunk-surface', 'tgs-trunk-lane-divider-left', 'tgs-trunk-lane-divider-right', 'tgs-trunk-median',
        'tgs-motorway-casing', 'tgs-motorway-surface', 'tgs-motorway-lane-divider-left', 'tgs-motorway-lane-divider-right', 'tgs-motorway-median',
        'tgs-bridge-casing', 'tgs-bridge-surface',
        // Workzones
        'workzone-fill', 'workzone-line',
        // Arrows
        'arrow-hit', 'arrow-lines', 'arrow-heads', 'arrow-bars', 'arrow-labels',
        // Delineators
        'delineator-lines-stroke',
        // Drawing preview
        'drawing-preview-line',
      ];

      for (const layerId of customLayerIds) {
        if (map.getLayer(layerId)) {
          try {
            map.moveLayer(FADE_LAYER_ID, layerId);
            fadeLayerPositionedRef.current = true;
            return; // Successfully positioned
          } catch (e) {
            // Layer ordering failed, ignore
          }
        }
      }

      // Fallback: If no custom layers exist yet, position fade just below road labels
      // This ensures it's above the satellite imagery but doesn't block labels
      // When custom layers are added, they'll be above the fade
      if (!fadeLayerPositionedRef.current) {
        try {
          // Position after the basemap layers - custom layers added via react-map-gl
          // will naturally be added after this, so they'll be above the fade
          fadeLayerPositionedRef.current = true;
        } catch (e) {
          // Ignore
        }
      }
    };

    // Debounced reposition check
    const scheduleReposition = () => {
      if (repositionTimeout) clearTimeout(repositionTimeout);
      repositionTimeout = setTimeout(() => {
        ensureFadeLayer();
        if (!fadeLayerPositionedRef.current) {
          positionFadeLayer();
        }
      }, 50);
    };

    // Set up event listeners
    const handleStyleLoad = () => {
      fadeLayerPositionedRef.current = false;
      ensureFadeLayer();
      scheduleReposition();
    };

    const handleSourceData = () => {
      scheduleReposition();
    };

    map.on('style.load', handleStyleLoad);
    map.on('sourcedata', handleSourceData);
    
    // Run immediately if ready
    if (map.isStyleLoaded()) {
      ensureFadeLayer();
      scheduleReposition();
    }

    return () => {
      map.off('style.load', handleStyleLoad);
      map.off('sourcedata', handleSourceData);
      if (repositionTimeout) clearTimeout(repositionTimeout);
    };
  }, [mapFadeOpacity]);

  // Re-position fade layer when custom layers are added/removed
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    const FADE_LAYER_ID = 'tgs-map-fade-overlay';
    
    const repositionFade = () => {
      if (!map.isStyleLoaded() || !map.getLayer(FADE_LAYER_ID)) return;

      const customLayerIds = [
        // Road markings (tgs-* layers)
        'tgs-surfaces-base', 'tgs-markings-base',
        'tgs-tunnel-casing', 'tgs-tunnel-surface',
        'tgs-service-casing', 'tgs-service-surface',
        'tgs-street-casing', 'tgs-street-surface', 'tgs-street-centerline', 'tgs-street-lane-divider',
        'tgs-secondary-casing', 'tgs-secondary-surface', 'tgs-secondary-centerline', 'tgs-secondary-lane-divider',
        'tgs-primary-casing', 'tgs-primary-surface', 'tgs-primary-lane-divider-left', 'tgs-primary-lane-divider-right',
        'tgs-trunk-casing', 'tgs-trunk-surface', 'tgs-trunk-lane-divider-left', 'tgs-trunk-lane-divider-right', 'tgs-trunk-median',
        'tgs-motorway-casing', 'tgs-motorway-surface', 'tgs-motorway-lane-divider-left', 'tgs-motorway-lane-divider-right', 'tgs-motorway-median',
        'tgs-bridge-casing', 'tgs-bridge-surface',
        // Workzones
        'workzone-fill', 'workzone-line',
        // Arrows
        'arrow-hit', 'arrow-lines', 'arrow-heads', 'arrow-bars', 'arrow-labels',
        // Delineators
        'delineator-lines-stroke',
      ];

      // Find the lowest custom layer in the stack and move fade below it
      const layers = map.getStyle()?.layers || [];
      const layerOrder = layers.map(l => l.id);
      
      let lowestCustomLayerIndex = -1;
      let lowestCustomLayerId = '';
      
      for (const layerId of customLayerIds) {
        const idx = layerOrder.indexOf(layerId);
        if (idx !== -1 && (lowestCustomLayerIndex === -1 || idx < lowestCustomLayerIndex)) {
          lowestCustomLayerIndex = idx;
          lowestCustomLayerId = layerId;
        }
      }

      if (lowestCustomLayerId) {
        const fadeIdx = layerOrder.indexOf(FADE_LAYER_ID);
        // Only move if fade is above the lowest custom layer
        if (fadeIdx > lowestCustomLayerIndex) {
          try {
            map.moveLayer(FADE_LAYER_ID, lowestCustomLayerId);
            fadeLayerPositionedRef.current = true;
          } catch (e) {
            console.warn('Failed to reposition fade layer:', e);
          }
        }
      }
    };
    
    // Wait for react-map-gl layers to be added, then reposition
    // Use multiple attempts with increasing delays to handle async layer addition
    const timeouts = [50, 150, 300].map(delay => 
      setTimeout(repositionFade, delay)
    );

    return () => timeouts.forEach(t => clearTimeout(t));
  }, [workzones.length, arrows.length, delineatorLines.length, showRoadMarkings]);

  // Query road features at sign locations for "show only near signs" filter
  // This finds which road segments have signs placed on them
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !roadOverlaySettings.showOnlyNearSigns || !showRoadMarkings) {
      // Clear the filter when disabled
      if (!roadOverlaySettings.showOnlyNearSigns) {
        setSignRoadFeatureIds(new Set());
      }
      return;
    }

    const signs = signPlacement?.signs || [];
    if (signs.length === 0) {
      setSignRoadFeatureIds(new Set());
      return;
    }

    const queryRoadFeatures = () => {
      if (!map.isStyleLoaded()) return;

      const roadIds = new Set<number>();
      
      for (const sign of signs) {
        // Query at a small area around the sign to catch nearby road features
        const point = map.project([sign.position.lng, sign.position.lat]);
        
        // Query a small box around the point (30 pixels) to ensure we catch the road
        const bbox: [mapboxgl.PointLike, mapboxgl.PointLike] = [
          [point.x - 30, point.y - 30],
          [point.x + 30, point.y + 30]
        ];
        
        // Query features from the road source layer
        const features = map.queryRenderedFeatures(bbox, {
          layers: undefined, // Query all layers
        });
        
        // Find road features from the composite source
        for (const feature of features) {
          if (feature.source === 'composite' && feature.sourceLayer === 'road') {
            // Use feature ID if available
            if (feature.id !== undefined) {
              roadIds.add(feature.id as number);
            }
          }
        }
      }
      
      setSignRoadFeatureIds(roadIds);
    };

    // Query immediately if map is ready
    if (map.isStyleLoaded()) {
      queryRoadFeatures();
    }

    // Also query when map moves or zoom changes (road features may change)
    const handleMapChange = () => {
      // Debounce the query
      setTimeout(queryRoadFeatures, 100);
    };

    map.on('moveend', handleMapChange);
    map.on('style.load', queryRoadFeatures);

    return () => {
      map.off('moveend', handleMapChange);
      map.off('style.load', queryRoadFeatures);
    };
  }, [roadOverlaySettings.showOnlyNearSigns, signPlacement?.signs, showRoadMarkings]);

  // Store original filters for road layers so we can restore them
  const originalRoadFiltersRef = useRef<Record<string, any>>({});

  // Apply feature ID filter to road overlay layers when "show only near signs" is enabled
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !showRoadMarkings) return;

    const TGS_ROAD_LAYERS = [
      'tgs-tunnel-casing', 'tgs-tunnel-surface',
      'tgs-service-casing', 'tgs-service-surface',
      'tgs-street-casing', 'tgs-street-surface', 'tgs-street-centerline', 'tgs-street-lane-divider',
      'tgs-secondary-casing', 'tgs-secondary-surface', 'tgs-secondary-centerline', 'tgs-secondary-lane-divider',
      'tgs-primary-casing', 'tgs-primary-surface', 'tgs-primary-lane-divider-left', 'tgs-primary-lane-divider-right',
      'tgs-trunk-casing', 'tgs-trunk-surface', 'tgs-trunk-lane-divider-left', 'tgs-trunk-lane-divider-right', 'tgs-trunk-median',
      'tgs-motorway-casing', 'tgs-motorway-surface', 'tgs-motorway-lane-divider-left', 'tgs-motorway-lane-divider-right', 'tgs-motorway-median',
      'tgs-bridge-casing', 'tgs-bridge-surface',
    ];

    const updateFilters = () => {
      if (!map.isStyleLoaded()) return;

      for (const layerId of TGS_ROAD_LAYERS) {
        const layer = map.getLayer(layerId);
        if (!layer) continue;

        // Store original filter if not already stored
        if (!(layerId in originalRoadFiltersRef.current)) {
          originalRoadFiltersRef.current[layerId] = (layer as any).filter;
        }

        const originalFilter = originalRoadFiltersRef.current[layerId];
        if (!originalFilter) continue;

        if (roadOverlaySettings.showOnlyNearSigns && signRoadFeatureIds.size > 0) {
          // Add feature ID filter: only show features whose ID is in our set
          const idArray = Array.from(signRoadFeatureIds);
          const newFilter = [
            'all',
            originalFilter,
            ['in', ['id'], ['literal', idArray]]
          ];
          try {
            map.setFilter(layerId, newFilter as any);
          } catch (e) {
            // Filter might be invalid, ignore
          }
        } else if (!roadOverlaySettings.showOnlyNearSigns) {
          // Restore original filter when feature is disabled
          try {
            map.setFilter(layerId, originalFilter);
          } catch (e) {
            // Ignore errors
          }
        }
      }
    };

    // Wait for layers to be added before updating filters
    const timeout = setTimeout(updateFilters, 200);
    
    return () => clearTimeout(timeout);
  }, [signRoadFeatureIds, roadOverlaySettings.showOnlyNearSigns, showRoadMarkings]);

  // Add click handlers for road overlay layers to open properties panel
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !showRoadMarkings) return;

    const TGS_ROAD_LAYERS = [
      'tgs-tunnel-casing', 'tgs-tunnel-surface',
      'tgs-service-casing', 'tgs-service-surface',
      'tgs-street-casing', 'tgs-street-surface', 'tgs-street-centerline', 'tgs-street-lane-divider',
      'tgs-secondary-casing', 'tgs-secondary-surface', 'tgs-secondary-centerline', 'tgs-secondary-lane-divider',
      'tgs-primary-casing', 'tgs-primary-surface', 'tgs-primary-lane-divider-left', 'tgs-primary-lane-divider-right',
      'tgs-trunk-casing', 'tgs-trunk-surface', 'tgs-trunk-lane-divider-left', 'tgs-trunk-lane-divider-right', 'tgs-trunk-median',
      'tgs-motorway-casing', 'tgs-motorway-surface', 'tgs-motorway-lane-divider-left', 'tgs-motorway-lane-divider-right', 'tgs-motorway-median',
      'tgs-bridge-casing', 'tgs-bridge-surface',
    ];

    const handleRoadClick = (e: mapboxgl.MapMouseEvent) => {
      // Select road overlay in properties panel
      onElementSelect?.({ type: 'roadoverlay', data: roadOverlaySettings });
    };

    const handleMouseEnter = () => {
      map.getCanvas().style.cursor = 'pointer';
    };

    const handleMouseLeave = () => {
      map.getCanvas().style.cursor = '';
    };

    const setupClickHandlers = () => {
      if (!map.isStyleLoaded()) return;

      for (const layerId of TGS_ROAD_LAYERS) {
        if (map.getLayer(layerId)) {
          map.on('click', layerId, handleRoadClick);
          // Change cursor to pointer on hover
          map.on('mouseenter', layerId, handleMouseEnter);
          map.on('mouseleave', layerId, handleMouseLeave);
        }
      }
    };

    // Wait for layers to be added
    const timeout = setTimeout(setupClickHandlers, 300);

    return () => {
      clearTimeout(timeout);
      // Clean up event listeners
      for (const layerId of TGS_ROAD_LAYERS) {
        if (map.getLayer(layerId)) {
          map.off('click', layerId, handleRoadClick);
          map.off('mouseenter', layerId, handleMouseEnter);
          map.off('mouseleave', layerId, handleMouseLeave);
        }
      }
    };
  }, [showRoadMarkings, roadOverlaySettings, onElementSelect]);

  // Provide map controls to parent - now triggered when map loads
  useEffect(() => {
    if (mapRef.current && onMapReady && isMapLoaded) {
      const map = mapRef.current.getMap();
      onMapReady({
        zoomIn: () => map.zoomIn(),
        zoomOut: () => map.zoomOut(),
        setDrawingMode: (mode) => {
          setDrawingMode(mode);
          // Clear drawing points when exiting drawing mode
          if (mode === null) {
            setDrawingPoints([]);
          }
        },
        snapToLocation: (location) => {
          const target = location || selectedLocation;
          if (target) {
            map.flyTo({
              center: [target.lng, target.lat],
              zoom: 19,
              duration: 1000,
            });
          }
        },
        getMapInstance: () => map,
        // Capture the map canvas and render signs/overlays on top at high resolution
        // Returns a sharp, crisp capture of the bounding box area
        // fadeOpacity: optional white overlay on the map (0-1), applied before signs so signs remain sharp
        captureCanvas: async (boundingBox?: { x: number; y: number; width: number; height: number }, fadeOpacity?: number): Promise<string | null> => {
          try {
            // Wait for any pending renders to complete
            await new Promise<void>((resolve) => {
              if (map.loaded() && !map.isMoving()) {
                resolve();
              } else {
                map.once('idle', () => resolve());
              }
            });
            
            // Force a render to ensure map is up to date
            map.triggerRepaint();
            await new Promise(resolve => setTimeout(resolve, 200));
            
            // Get the Mapbox canvas
            const mapCanvas = map.getCanvas();
            if (!mapCanvas) {
              console.error('Map canvas not available');
              return null;
            }
            
            const container = mapContainerRef.current;
            if (!container) {
              console.error('Map container ref not available');
              return null;
            }
            
            // Get container rect for coordinate calculations
            const containerRect = container.getBoundingClientRect();
            
            // High-resolution scale factor for sharp output (2x = retina quality)
            const exportScale = 4;
            
            // Calculate logical dimensions (screen coordinates)
            const logicalWidth = boundingBox ? Math.round(boundingBox.width) : Math.round(containerRect.width);
            const logicalHeight = boundingBox ? Math.round(boundingBox.height) : Math.round(containerRect.height);
            
            // Create high-resolution output canvas
            const outputCanvas = document.createElement('canvas');
            outputCanvas.width = logicalWidth * exportScale;
            outputCanvas.height = logicalHeight * exportScale;
            
            const ctx = outputCanvas.getContext('2d');
            if (!ctx) {
              console.error('Failed to get 2D context');
              return null;
            }
            
            // Enable high-quality image rendering
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            
            // Scale all drawing operations
            ctx.scale(exportScale, exportScale);
            
            // Calculate the offset for cropping
            const offsetX = boundingBox ? Math.round(boundingBox.x) : 0;
            const offsetY = boundingBox ? Math.round(boundingBox.y) : 0;
            
            // Account for device pixel ratio of the source canvas
            const dpr = window.devicePixelRatio || 1;
            
            // Draw the map canvas (cropped to bounding box if provided)
            // Source is in device pixels, destination is in logical pixels (scaled by ctx.scale)
            // Note: The fade overlay is now a Mapbox GL layer, so it's already rendered on the canvas
            ctx.drawImage(
              mapCanvas,
              offsetX * dpr, offsetY * dpr, logicalWidth * dpr, logicalHeight * dpr,  // Source (device pixels)
              0, 0, logicalWidth, logicalHeight  // Destination (logical pixels, scaled by ctx)
            );
            
            // Now render all markers (signs) on top at high resolution
            const markers = Array.from(container.querySelectorAll('.mapboxgl-marker'));
            console.log(`Found ${markers.length} markers to render at ${exportScale}x resolution`);
            
            for (const marker of markers) {
              const markerEl = marker as HTMLElement;
              
              // Get marker's position from transform
              const transform = markerEl.style.transform;
              const translateMatch = transform.match(/translate\((-?[\d.]+)px,\s*(-?[\d.]+)px\)/);
              if (!translateMatch) continue;
              
              const markerX = parseFloat(translateMatch[1]);
              const markerY = parseFloat(translateMatch[2]);
              
              // Adjust for bounding box offset
              const relativeX = markerX - offsetX;
              const relativeY = markerY - offsetY;
              
              // Skip markers outside the bounding box (with margin)
              if (boundingBox && (relativeX < -150 || relativeX > logicalWidth + 150 || 
                  relativeY < -150 || relativeY > logicalHeight + 150)) {
                continue;
              }
              
              // Render dot as empty circle with black outline
              const dot = markerEl.querySelector('div[title="Drag dot to move sign"]') as HTMLElement;
              if (dot) {
                const dotStyle = window.getComputedStyle(dot);
                const dotWidth = parseFloat(dotStyle.width);
                const dotHeight = parseFloat(dotStyle.height);
                const dotLeft = parseFloat(dotStyle.left);
                const dotTop = parseFloat(dotStyle.top);
                
                ctx.save();
                ctx.beginPath();
                ctx.arc(relativeX + dotLeft + dotWidth/2, relativeY + dotTop + dotHeight/2, dotWidth/2, 0, Math.PI * 2);
                // Empty circle with black outline (no fill)
                ctx.strokeStyle = '#000000';
                ctx.lineWidth = 2;
                ctx.stroke();
                ctx.restore();
              }
              
              // Render leader line as solid black
              const svg = markerEl.querySelector('svg');
              if (svg) {
                const line = svg.querySelector('line');
                if (line) {
                  const x2 = parseFloat(line.getAttribute('x2') || '0');
                  const y2 = parseFloat(line.getAttribute('y2') || '0');
                  
                  ctx.save();
                  ctx.beginPath();
                  ctx.moveTo(relativeX, relativeY);
                  ctx.lineTo(relativeX + x2, relativeY + y2);
                  ctx.strokeStyle = '#000000';
                  ctx.lineWidth = 1.5;
                  // Solid line (no dash)
                  ctx.stroke();
                  ctx.restore();
                }
              }
              
              // Render sign image at high quality
              const signImg = markerEl.querySelector('img[draggable="false"]') as HTMLImageElement;
              if (signImg && signImg.src) {
                const imgStyle = window.getComputedStyle(signImg);
                const imgWidth = parseFloat(imgStyle.width);
                const imgHeight = parseFloat(imgStyle.height);
                
                // Get position from parent div
                const parentDiv = signImg.parentElement as HTMLElement;
                if (parentDiv) {
                  const parentStyle = window.getComputedStyle(parentDiv);
                  const parentLeft = parseFloat(parentStyle.left);
                  const parentTop = parseFloat(parentStyle.top);
                  
                  // Draw the image with high-quality interpolation
                  try {
                    ctx.drawImage(
                      signImg,
                      relativeX + parentLeft,
                      relativeY + parentTop,
                      imgWidth,
                      imgHeight
                    );
                  } catch (e) {
                    console.warn('Failed to draw sign image:', e);
                  }
                }
              }
            }
            
            console.log(`Captured canvas: ${outputCanvas.width}x${outputCanvas.height} (${exportScale}x scale) with ${markers.length} markers`);
            return outputCanvas.toDataURL('image/png');
            
          } catch (error) {
            console.error('Error capturing map canvas:', error);
            return null;
          }
        },
      });
    }
  }, [onMapReady, selectedLocation, isMapLoaded]);

  // Bounding box overlay handlers
  const handleConfirmBoundingBox = useCallback(() => {
    if (!boundingBoxOverlay || !mapRef.current) return;
    
    const map = mapRef.current.getMap();
    
    // Convert screen corners to geographic coordinates
    const topLeft = map.unproject([boundingBoxOverlay.x, boundingBoxOverlay.y]);
    const topRight = map.unproject([boundingBoxOverlay.x + boundingBoxOverlay.width, boundingBoxOverlay.y]);
    const bottomRight = map.unproject([boundingBoxOverlay.x + boundingBoxOverlay.width, boundingBoxOverlay.y + boundingBoxOverlay.height]);
    const bottomLeft = map.unproject([boundingBoxOverlay.x, boundingBoxOverlay.y + boundingBoxOverlay.height]);
    
    // Calculate axis-aligned bounding box from the 4 corners
    const lats = [topLeft.lat, topRight.lat, bottomRight.lat, bottomLeft.lat];
    const lngs = [topLeft.lng, topRight.lng, bottomRight.lng, bottomLeft.lng];
    
    const bounds = {
      northeast: {
        lat: Math.max(...lats),
        lng: Math.max(...lngs),
      },
      southwest: {
        lat: Math.min(...lats),
        lng: Math.min(...lngs),
      },
      // Also include the actual corners for more precise rendering
      corners: {
        topLeft: { lat: topLeft.lat, lng: topLeft.lng },
        topRight: { lat: topRight.lat, lng: topRight.lng },
        bottomRight: { lat: bottomRight.lat, lng: bottomRight.lng },
        bottomLeft: { lat: bottomLeft.lat, lng: bottomLeft.lng },
      },
      // Include camera settings
      camera: {
        zoom: map.getZoom(),
        pitch: map.getPitch(),
        bearing: map.getBearing(),
      },
      // IMPORTANT: Include screen pixel coordinates for canvas capture/cropping
      screenBounds: {
        x: boundingBoxOverlay.x,
        y: boundingBoxOverlay.y,
        width: boundingBoxOverlay.width,
        height: boundingBoxOverlay.height,
      },
    };
    
    onPrintAreaSelect?.(bounds);
    onAreaSelected?.(bounds);
    
    // Clear overlay
    setBoundingBoxOverlay(null);
    setDrawingMode(null);
  }, [boundingBoxOverlay, onPrintAreaSelect, onAreaSelected]);

  const handleCancelBoundingBox = useCallback(() => {
    setBoundingBoxOverlay(null);
    setDrawingMode(null);
  }, []);

  const handleOverlayDragStart = useCallback((e: React.MouseEvent) => {
    if (!boundingBoxOverlay) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOverlay(true);
    setDragStart({
      x: e.clientX,
      y: e.clientY,
      boxX: boundingBoxOverlay.x,
      boxY: boundingBoxOverlay.y,
    });
  }, [boundingBoxOverlay]);


  const handleOverlayMouseMove = useCallback((e: React.MouseEvent) => {
    // Handle bounding box overlay dragging (no resizing - size is fixed by paper dimensions)
    if (isDraggingOverlay && dragStart && boundingBoxOverlay) {
      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;
      setBoundingBoxOverlay(prev => prev ? {
        ...prev,
        x: dragStart.boxX + dx,
        y: dragStart.boxY + dy,
      } : null);
    }
    
    // Handle sign callout dragging (inline to avoid hoisting issues)
    if (draggingCallout) {
      const deltaX = e.clientX - draggingCallout.startMouseX;
      const deltaY = e.clientY - draggingCallout.startMouseY;
      setDraggingCallout(prev => prev ? {
        ...prev,
        currentOffsetX: prev.startOffsetX + deltaX,
        currentOffsetY: prev.startOffsetY + deltaY,
      } : null);
    }
  }, [isDraggingOverlay, dragStart, boundingBoxOverlay, draggingCallout]);

  const handleOverlayMouseUp = useCallback(() => {
    setIsDraggingOverlay(false);
    setDragStart(null);
    
    // Handle sign callout drag end (inline to avoid hoisting issues)
    if (draggingCallout && onSignCalloutMove) {
      onSignCalloutMove(draggingCallout.signId, {
        x: draggingCallout.currentOffsetX,
        y: draggingCallout.currentOffsetY,
      });
      setDraggingCallout(null);
    }
  }, [draggingCallout, onSignCalloutMove]);

  // Search functionality using Mapbox Geocoding API
  const handleSearch = useCallback(async () => {
    if (!searchText.trim() || !apiKey) return;
    
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(searchText)}.json?access_token=${apiKey}&country=au&limit=1`
      );
      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        const feature = data.features[0];
        const [lng, lat] = feature.center;
        
        setSelectedLocation({ lat, lng });
        
        if (mapRef.current) {
          mapRef.current.flyTo({
            center: [lng, lat],
            zoom: 19,
            duration: 1000,
          });
        }
        
        // Extract city from context
        const cityContext = feature.context?.find((c: any) => c.id.startsWith('place.'));
        const cityName = cityContext?.text || null;
        
        onWorkzoneSelect({
          location: { lat, lng },
          address: feature.place_name,
          name: feature.text,
          city: cityName,
          state: 'SA',
          country: 'Australia',
        });
      }
    } catch (error) {
      console.error('Search error:', error);
    }
  }, [searchText, apiKey, onWorkzoneSelect]);

  // Handle map clicks for drawing and arrows
  const handleMapClick = useCallback((event: MapMouseEvent) => {
    const { lng, lat } = event.lngLat;
    const point: LatLng = { lat, lng };

    // Clear any drag state on map click
    setDraggingCallout(null);

    // Handle clicks on arrow layers (line or head)
    const hitFeature = event.features?.find(
      (feature) =>
        feature.layer?.id === 'arrow-hit' ||
        feature.layer?.id === 'arrow-lines' ||
        feature.layer?.id === 'arrow-heads' ||
        feature.layer?.id === 'arrow-bars' ||
        feature.layer?.id === 'arrow-labels'
    );
    if (hitFeature) {
      const arrowId = hitFeature.properties?.arrowId || hitFeature.properties?.id;
      if (arrowId) {
        const arrow = arrows.find((item) => item.id === arrowId);
        if (arrow) {
          onElementSelect?.({ type: 'arrow', id: arrow.id, data: arrow });
          return;
        }
      }
    }

    // Check if the click originated from a marker/overlay element (not the map canvas itself)
    // This prevents clearing selection when clicking on signs, arrows, delineators, etc.
    const target = event.originalEvent?.target as HTMLElement | null;
    const isMapCanvas = target?.classList?.contains('mapboxgl-canvas') || 
                        target?.tagName?.toLowerCase() === 'canvas';
    
    // Clear selection when clicking on empty map area (if not in a drawing mode)
    // Only clear if we actually clicked on the map canvas, not on an overlay element
    if (currentTool === 'cursor' && !drawingMode && isMapCanvas) {
      onClearSelection?.();
    }

    // Handle arrow drawing
    if (currentTool === 'arrow') {
      const draftStart = arrowDraftRef.current?.start ?? null;
      
      if (!draftStart) {
        arrowDraftRef.current = { start: point };
        onArrowDraftStart?.(point);
        return;
      }
      
      const rawDistance = haversineDistance(draftStart.lat, draftStart.lng, point.lat, point.lng);
      const distance = Number.isFinite(rawDistance) ? Number(rawDistance.toFixed(1)) : 0;
      arrowDraftRef.current = null;
      onArrowComplete?.({ start: draftStart, end: point, distance });
      setTempArrowEnd(null);
      return;
    }

    // Handle delineator drawing - single click adds a point
    if (currentTool === 'delineator' && delineatorDraft) {
      onDelineatorDraftAddPoint?.(point);
      return;
    }

    // Handle polygon drawing
    if (drawingMode === 'polygon') {
      setDrawingPoints(prev => [...prev, point]);
    }
    
    // Rectangle drawing is now handled by the screen-space overlay
    // No click handling needed for rectangles
  }, [arrows, currentTool, drawingMode, onArrowDraftStart, onArrowComplete, delineatorDraft, onDelineatorDraftAddPoint, onClearSelection, onElementSelect]);

  // Handle mouse down for starting arrow drag
  const handleMapMouseDown = useCallback((event: MapMouseEvent) => {
    if (currentTool !== 'cursor') return;
    
    const { lng, lat } = event.lngLat;
    const map = mapRef.current?.getMap();
    if (!map) return;
    
    // Query features at the click point - check if layers exist first
    const layerIds = ['arrow-hit', 'arrow-lines', 'arrow-heads', 'arrow-bars'];
    const existingLayers = layerIds.filter(id => map.getLayer(id));
    if (existingLayers.length === 0) return;
    
    const features = map.queryRenderedFeatures(event.point, {
      layers: existingLayers,
    });
    
    const hitFeature = features?.[0];
    
    if (hitFeature) {
      const arrowId = hitFeature.properties?.arrowId || hitFeature.properties?.id;
      if (arrowId) {
        const arrow = arrows.find((item) => item.id === arrowId);
        if (arrow && onArrowMove) {
          // Prevent map panning while dragging
          event.preventDefault();
          
          // Start dragging
          setDraggingArrow({
            arrowId: arrow.id,
            startMouseLat: lat,
            startMouseLng: lng,
            originalStart: { ...arrow.start },
            originalEnd: { ...arrow.end },
          });
          // Also select it
          onElementSelect?.({ type: 'arrow', id: arrow.id, data: arrow });
        }
      }
    }
  }, [arrows, currentTool, onArrowMove, onElementSelect]);

  // Handle mouse move for preview and arrow dragging
  const handleMapMouseMove = useCallback((event: MapMouseEvent) => {
    const { lng, lat } = event.lngLat;
    
    // Handle arrow dragging - update local offset for smooth visuals
    if (draggingArrow) {
      const deltaLat = lat - draggingArrow.startMouseLat;
      const deltaLng = lng - draggingArrow.startMouseLng;
      setArrowDragOffset({ lat: deltaLat, lng: deltaLng });
      return;
    }
    
    // Arrow preview
    if (currentTool === 'arrow' && arrowDraftRef.current?.start) {
      setTempArrowEnd({ lat, lng });
    }
    
    // Delineator preview - show line to cursor
    if (currentTool === 'delineator' && delineatorDraft && delineatorDraft.path.length > 0) {
      setTempDelineatorEnd({ lat, lng });
    } else {
      setTempDelineatorEnd(null);
    }
    
    // Rectangle preview is now handled by the screen-space overlay
  }, [currentTool, delineatorDraft, draggingArrow]);

  // Handle mouse up to end arrow dragging
  const handleMapMouseUp = useCallback(() => {
    if (draggingArrow && arrowDragOffset && onArrowMove) {
      // Apply the final position
      const newStart = {
        lat: draggingArrow.originalStart.lat + arrowDragOffset.lat,
        lng: draggingArrow.originalStart.lng + arrowDragOffset.lng,
      };
      const newEnd = {
        lat: draggingArrow.originalEnd.lat + arrowDragOffset.lat,
        lng: draggingArrow.originalEnd.lng + arrowDragOffset.lng,
      };
      onArrowMove(draggingArrow.arrowId, newStart, newEnd);
    }
    setDraggingArrow(null);
    setArrowDragOffset(null);
  }, [draggingArrow, arrowDragOffset, onArrowMove]);

  // Global mouseup listener to handle releasing outside the map
  useEffect(() => {
    if (!draggingArrow) return;
    
    const handleGlobalMouseUp = () => {
      // Apply final position if we have an offset
      if (arrowDragOffset && onArrowMove) {
        const newStart = {
          lat: draggingArrow.originalStart.lat + arrowDragOffset.lat,
          lng: draggingArrow.originalStart.lng + arrowDragOffset.lng,
        };
        const newEnd = {
          lat: draggingArrow.originalEnd.lat + arrowDragOffset.lat,
          lng: draggingArrow.originalEnd.lng + arrowDragOffset.lng,
        };
        onArrowMove(draggingArrow.arrowId, newStart, newEnd);
      }
      setDraggingArrow(null);
      setArrowDragOffset(null);
    };
    
    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
  }, [draggingArrow, arrowDragOffset, onArrowMove]);

  // Native mousedown listener on arrow layers for drag functionality
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || currentTool !== 'cursor') return;

    const handleArrowMouseDown = (e: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] }) => {
      const features = e.features;
      if (!features || features.length === 0) return;
      
      const hitFeature = features[0];
      const arrowId = hitFeature.properties?.arrowId || hitFeature.properties?.id;
      
      if (arrowId && onArrowMove) {
        const arrow = arrows.find((item) => item.id === arrowId);
        if (arrow) {
          e.preventDefault();
          
          setDraggingArrow({
            arrowId: arrow.id,
            startMouseLat: e.lngLat.lat,
            startMouseLng: e.lngLat.lng,
            originalStart: { ...arrow.start },
            originalEnd: { ...arrow.end },
          });
          
          onElementSelect?.({ type: 'arrow', id: arrow.id, data: arrow });
        }
      }
    };

    // Add mousedown listener to arrow layers
    map.on('mousedown', 'arrow-hit', handleArrowMouseDown);
    map.on('mousedown', 'arrow-lines', handleArrowMouseDown);
    map.on('mousedown', 'arrow-heads', handleArrowMouseDown);
    map.on('mousedown', 'arrow-bars', handleArrowMouseDown);

    return () => {
      map.off('mousedown', 'arrow-hit', handleArrowMouseDown);
      map.off('mousedown', 'arrow-lines', handleArrowMouseDown);
      map.off('mousedown', 'arrow-heads', handleArrowMouseDown);
      map.off('mousedown', 'arrow-bars', handleArrowMouseDown);
    };
  }, [arrows, currentTool, onArrowMove, onElementSelect]);

  const ensureArrowheadImage = useCallback((map: mapboxgl.Map) => {
    const imageId = 'arrowhead-icon';
    if (map.hasImage(imageId)) return;

    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12">
        <path d="M6 0 L12 12 L0 12 Z" fill="#000000" />
      </svg>
    `;

    const img = new Image(12, 12);
    img.onload = () => {
      if (!map.hasImage(imageId)) {
        map.addImage(imageId, img, { pixelRatio: 2 });
      }
    };
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  }, []);

  const ensureArrowBarImage = useCallback((map: mapboxgl.Map) => {
    const imageId = 'arrowbar-icon';
    if (map.hasImage(imageId)) return;

    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12">
        <rect x="5" y="1" width="2" height="10" fill="#000000" />
      </svg>
    `;

    const img = new Image(12, 12);
    img.onload = () => {
      if (!map.hasImage(imageId)) {
        map.addImage(imageId, img, { pixelRatio: 2 });
      }
    };
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  }, []);

  useEffect(() => {
    const map = mapRef.current?.getMap?.();
    if (!map) return;
    ensureArrowheadImage(map);
    ensureArrowBarImage(map);
    const handleStyleData = () => {
      ensureArrowheadImage(map);
      ensureArrowBarImage(map);
    };
    map.on('styledata', handleStyleData);
    return () => {
      map.off('styledata', handleStyleData);
    };
  }, [ensureArrowheadImage, ensureArrowBarImage]);

  // Handle double-click to complete polygon or delineator
  const handleMapDoubleClick = useCallback((event: MapMouseEvent) => {
    // Complete delineator line on double-click
    if (currentTool === 'delineator' && delineatorDraft && delineatorDraft.path.length >= 2) {
      onDelineatorComplete?.({
        deviceType: delineatorDraft.deviceType,
        path: delineatorDraft.path,
        spacing: delineatorDraft.spacing,
        deviceSize: delineatorDraft.deviceSize,
        geometryType: delineatorDraft.geometryType,
      });
      setTempDelineatorEnd(null);
      return;
    }
    
    if (drawingMode === 'polygon' && drawingPoints.length >= 3) {
      // Compute polygon center as fallback if no selectedLocation
      let workzoneLocation = selectedLocation;
      
      if (!workzoneLocation && drawingPoints.length > 0) {
        // Calculate centroid of polygon
        const sumLat = drawingPoints.reduce((sum, c) => sum + c.lat, 0);
        const sumLng = drawingPoints.reduce((sum, c) => sum + c.lng, 0);
        workzoneLocation = {
          lat: sumLat / drawingPoints.length,
          lng: sumLng / drawingPoints.length
        };
      }
      
      onWorkzoneSelect({
        polygon: drawingPoints,
        coordinates: drawingPoints,
        location: workzoneLocation,
        city: null,
        state: 'SA',
        country: 'Australia',
      });
      
      setDrawingPoints([]);
      setDrawingMode(null);
      onWorkzoneDrawingComplete?.();
    }
  }, [currentTool, delineatorDraft, onDelineatorComplete, drawingMode, drawingPoints, selectedLocation, onWorkzoneSelect, onWorkzoneDrawingComplete]);

  // Format distance helper
  const formatDistance = useCallback((value: number) => {
    if (!Number.isFinite(value)) return '0';
    return Number.isInteger(value) ? `${value}` : value.toFixed(1);
  }, []);

  // Handle delineator line drag end - moves entire line
  const handleDelineatorDragEnd = useCallback((
    lineId: string,
    originalPath: LatLng[],
    event: MarkerDragEvent
  ) => {
    if (!onDelineatorUpdate || !draggingDelineator) return;
    
    const { lng, lat } = event.lngLat;
    
    // Calculate offset from original first point
    const offsetLat = lat - draggingDelineator.startLat;
    const offsetLng = lng - draggingDelineator.startLng;
    
    // Apply offset to all path points
    const newPath = originalPath.map(p => ({
      lat: p.lat + offsetLat,
      lng: p.lng + offsetLng,
    }));
    
    onDelineatorUpdate(lineId, { path: newPath });
    setDraggingDelineator(null);
  }, [onDelineatorUpdate, draggingDelineator]);

  // Handle callout drag start
  const handleCalloutDragStart = useCallback((
    signId: string,
    currentOffsetX: number,
    currentOffsetY: number,
    e: React.MouseEvent
  ) => {
    // Don't start drag if not in manual mode
    if (!isManualMode) return;
    e.stopPropagation();
    e.preventDefault();
    setDraggingCallout({
      signId,
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      startOffsetX: currentOffsetX,
      startOffsetY: currentOffsetY,
      currentOffsetX,
      currentOffsetY,
    });
  }, [isManualMode]);

  // Handle callout drag move (called from map container mouse move)
  const handleCalloutDragMove = useCallback((e: React.MouseEvent) => {
    if (!draggingCallout) return;
    
    const deltaX = e.clientX - draggingCallout.startMouseX;
    const deltaY = e.clientY - draggingCallout.startMouseY;
    
    setDraggingCallout(prev => prev ? {
      ...prev,
      currentOffsetX: prev.startOffsetX + deltaX,
      currentOffsetY: prev.startOffsetY + deltaY,
    } : null);
  }, [draggingCallout]);

  // Handle callout drag end
  const handleCalloutDragEnd = useCallback(() => {
    if (!draggingCallout || !onSignCalloutMove) return;
    
    onSignCalloutMove(draggingCallout.signId, {
      x: draggingCallout.currentOffsetX,
      y: draggingCallout.currentOffsetY,
    });
    setDraggingCallout(null);
  }, [draggingCallout, onSignCalloutMove]);

  // Snap sign to nearest road edge with auto-rotation to face traffic
  const snapToRoadEdge = useCallback((
    map: mapboxgl.Map,
    dropPoint: { x: number; y: number },
    dropLngLat: { lng: number; lat: number }
  ): SnapResult => {
    const zoom = map.getZoom();
    
    // Search radius in pixels - larger at lower zoom levels
    const searchRadius = Math.max(30, Math.min(100, 150 - zoom * 5));
    
    // Query our TGS road surface layers near the drop point
    const roadLayers = [
      'tgs-motorway-surface', 'tgs-trunk-surface', 'tgs-primary-surface',
      'tgs-secondary-surface', 'tgs-street-surface', 'tgs-service-surface',
      'tgs-bridge-surface', 'tgs-tunnel-surface'
    ];
    
    // Filter to only layers that exist on the map
    const existingLayers = roadLayers.filter(layer => map.getLayer(layer));
    
    if (existingLayers.length === 0) {
      // No road layers found, return original position with no edge metadata
      return { 
        lat: dropLngLat.lat, 
        lng: dropLngLat.lng, 
        rotation: 0,
        edgeMetadata: {
          roadSegmentId: '',
          side: 'right',
          positionAlongEdge: 0,
          edgeAngle: 0,
        }
      };
    }
    
    // Query features in a box around the drop point
    const features = map.queryRenderedFeatures(
      [
        [dropPoint.x - searchRadius, dropPoint.y - searchRadius],
        [dropPoint.x + searchRadius, dropPoint.y + searchRadius]
      ],
      { layers: existingLayers }
    );
    
    if (!features || features.length === 0) {
      // No roads found nearby, return original position with no edge metadata
      return { 
        lat: dropLngLat.lat, 
        lng: dropLngLat.lng, 
        rotation: 0,
        edgeMetadata: {
          roadSegmentId: '',
          side: 'right',
          positionAlongEdge: 0,
          edgeAngle: 0,
        }
      };
    }
    
    // Helper: calculate distance from point to line segment
    const pointToSegmentDistance = (
      px: number, py: number,
      x1: number, y1: number,
      x2: number, y2: number
    ): { distance: number; closestX: number; closestY: number; t: number } => {
      const dx = x2 - x1;
      const dy = y2 - y1;
      const lengthSq = dx * dx + dy * dy;
      
      if (lengthSq === 0) {
        return { distance: Math.sqrt((px - x1) ** 2 + (py - y1) ** 2), closestX: x1, closestY: y1, t: 0 };
      }
      
      // Parameter t represents position along segment (0 = start, 1 = end)
      let t = ((px - x1) * dx + (py - y1) * dy) / lengthSq;
      t = Math.max(0, Math.min(1, t));
      
      const closestX = x1 + t * dx;
      const closestY = y1 + t * dy;
      const distance = Math.sqrt((px - closestX) ** 2 + (py - closestY) ** 2);
      
      return { distance, closestX, closestY, t };
    };
    
    // Helper: determine which side of the line a point is on
    // In screen coordinates (Y increases downward), cross product sign is inverted
    const getSideOfLine = (
      px: number, py: number,
      x1: number, y1: number,
      x2: number, y2: number
    ): 'left' | 'right' => {
      // Cross product in screen coords: positive = RIGHT of line, negative = LEFT
      // (opposite of standard math coords because Y-axis is flipped)
      const cross = (x2 - x1) * (py - y1) - (y2 - y1) * (px - x1);
      return cross > 0 ? 'right' : 'left';
    };
    
    // Find the closest road segment
    let closestFeature: mapboxgl.MapboxGeoJSONFeature | null = null;
    let closestSegment: { x1: number; y1: number; x2: number; y2: number } | null = null;
    let closestInfo: { distance: number; closestX: number; closestY: number; t: number } | null = null;
    let minDistance = Infinity;
    
    for (const feature of features) {
      if (feature.geometry.type !== 'LineString' && feature.geometry.type !== 'MultiLineString') {
        continue;
      }
      
      const coordinates = feature.geometry.type === 'LineString' 
        ? [feature.geometry.coordinates]
        : feature.geometry.coordinates;
      
      for (const line of coordinates) {
        for (let i = 0; i < line.length - 1; i++) {
          const [lng1, lat1] = line[i] as [number, number];
          const [lng2, lat2] = line[i + 1] as [number, number];
          
          // Convert to screen coordinates for distance calculation
          const p1 = map.project([lng1, lat1]);
          const p2 = map.project([lng2, lat2]);
          
          const info = pointToSegmentDistance(dropPoint.x, dropPoint.y, p1.x, p1.y, p2.x, p2.y);
          
          if (info.distance < minDistance) {
            minDistance = info.distance;
            closestFeature = feature;
            closestSegment = { x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y };
            closestInfo = info;
          }
        }
      }
    }
    
    if (!closestFeature || !closestSegment || !closestInfo) {
      return { 
        lat: dropLngLat.lat, 
        lng: dropLngLat.lng, 
        rotation: 0,
        edgeMetadata: {
          roadSegmentId: '',
          side: 'right',
          positionAlongEdge: 0,
          edgeAngle: 0,
        }
      };
    }
    
    // Determine road properties
    const props = closestFeature.properties || {};
    const isOneway = props.oneway === 'true' || props.oneway === true || props.oneway === 1;
    const lanes = props.lanes || 2;
    const roadClass = props.class || 'street';
    
// Reference zoom level for calculating edge offset
    // Signs will align perfectly with road edges at this zoom level
    // We use zoom 16 as reference since it's in the middle of the typical working range (14-19)
    const REFERENCE_ZOOM = 16;
    
    // Get the visual road half-width at a specific zoom (in pixels)
    const getVisualRoadHalfWidthAtZoom = (z: number, numLanes: number): number => {
      const stops: [number, number][] = [
        [12, 4], [14, 7], [16, 12], [18, 22], [20, 45]
      ];
      let widthPerLane = stops[0][1];
      if (z <= stops[0][0]) {
        widthPerLane = stops[0][1];
      } else if (z >= stops[stops.length - 1][0]) {
        widthPerLane = stops[stops.length - 1][1];
      } else {
        for (let i = 0; i < stops.length - 1; i++) {
          if (z >= stops[i][0] && z <= stops[i + 1][0]) {
            const t = (z - stops[i][0]) / (stops[i + 1][0] - stops[i][0]);
            widthPerLane = stops[i][1] + t * (stops[i + 1][1] - stops[i][1]);
            break;
          }
        }
      }
      return (numLanes * widthPerLane) / 2;
    };

    // Add margin outside the road edge (in meters)
    const edgeMarginMeters = 0; // Snap directly to the road edge
    
    // Determine which side of the road the sign was dropped on
    const side = getSideOfLine(
      dropPoint.x, dropPoint.y,
      closestSegment.x1, closestSegment.y1,
      closestSegment.x2, closestSegment.y2
    );
    
    // Get the closest point on centerline in lat/lng
    const closestLngLat = map.unproject([closestInfo.closestX, closestInfo.closestY]);
    
    // Calculate the road direction in geographic coordinates
    // We need to find the perpendicular direction in lat/lng space
    const startLngLat = map.unproject([closestSegment.x1, closestSegment.y1]);
    const endLngLat = map.unproject([closestSegment.x2, closestSegment.y2]);
    
    // Direction vector in geographic coords
    const geoSegmentDx = endLngLat.lng - startLngLat.lng;
    const geoSegmentDy = endLngLat.lat - startLngLat.lat;
    const geoSegmentLength = Math.sqrt(geoSegmentDx * geoSegmentDx + geoSegmentDy * geoSegmentDy);
    
    if (geoSegmentLength === 0) {
      return { 
        lat: dropLngLat.lat, 
        lng: dropLngLat.lng, 
        rotation: 0,
        edgeMetadata: {
          roadSegmentId: '',
          side: 'right',
          positionAlongEdge: 0,
          edgeAngle: 0,
        }
      };
    }
    
    // Perpendicular unit vector in geographic space
    // Note: In lat/lng, we need to account for longitude scaling with latitude
    const latRadians = closestLngLat.lat * Math.PI / 180;
    const lngScale = Math.cos(latRadians); // Longitude degrees are shorter at higher latitudes
    
    // Normalized perpendicular (rotated 90 degrees clockwise for RIGHT side of travel)
    // In geographic coords (lat increases UP), we need opposite rotation vs screen coords (Y increases DOWN)
    const geoPerpLng = geoSegmentDy / geoSegmentLength;
    const geoPerpLat = -geoSegmentDx / geoSegmentLength;
    
    // Convert offset from meters to degrees
    // 1 degree of latitude ≈ 111,320 meters
    // 1 degree of longitude ≈ 111,320 * cos(latitude) meters
    const METERS_PER_DEGREE_LAT = 111320;
    const metersPerDegreeLng = METERS_PER_DEGREE_LAT * lngScale;
    
    // Calculate road half-width in METERS based on the REFERENCE zoom level
    // This ensures signs align with road edges at commonly-used zoom levels
    const EARTH_CIRCUMFERENCE = 40075016.686; // meters
    const metersPerPixelAtRefZoom = (EARTH_CIRCUMFERENCE * Math.cos(latRadians)) / (256 * Math.pow(2, REFERENCE_ZOOM));
    const visualHalfWidthPixelsAtRef = getVisualRoadHalfWidthAtZoom(REFERENCE_ZOOM, lanes);
    const roadHalfWidthMeters = visualHalfWidthPixelsAtRef * metersPerPixelAtRefZoom;
    
    // Calculate distances to centerline and edges (in pixels for comparison)
    const distToCenterline = closestInfo.distance; // Already calculated - distance from drop to centerline
    
    // Calculate road half-width in PIXELS using the EXACT same interpolation as our road surface layers
    // This matches the visual width of the rendered road at CURRENT zoom (for snap target detection only)
    const getVisualRoadHalfWidthPixels = (z: number, numLanes: number): number => {
      // Interpolation stops matching our road surface layers (line-width per lane)
      const stops: [number, number][] = [
        [12, 4],
        [14, 7],
        [16, 12],
        [18, 22],
        [20, 45]
      ];
      
      let widthPerLane: number = stops[0][1]; // Default to first stop
      
      if (z <= stops[0][0]) {
        widthPerLane = stops[0][1];
      } else if (z >= stops[stops.length - 1][0]) {
        widthPerLane = stops[stops.length - 1][1];
      } else {
        // Linear interpolation between stops
        for (let i = 0; i < stops.length - 1; i++) {
          const [z1, w1] = stops[i];
          const [z2, w2] = stops[i + 1];
          if (z >= z1 && z <= z2) {
            const t = (z - z1) / (z2 - z1);
            widthPerLane = w1 + t * (w2 - w1);
            break;
          }
        }
      }
      
      // Total road width = lanes * widthPerLane, half-width = total / 2
      return (numLanes * widthPerLane) / 2;
    };
    
    const roadHalfWidthPixels = getVisualRoadHalfWidthPixels(zoom, lanes);
    
    // Calculate perpendicular unit vector in screen space
    const screenSegLen = Math.sqrt(
      Math.pow(closestSegment.x2 - closestSegment.x1, 2) + 
      Math.pow(closestSegment.y2 - closestSegment.y1, 2)
    );
    const screenPerpX = -(closestSegment.y2 - closestSegment.y1) / screenSegLen;
    const screenPerpY = (closestSegment.x2 - closestSegment.x1) / screenSegLen;
    
    // Edge positions in screen space (perpendicular offset from centerline)
    const edgeRightX = closestInfo.closestX + screenPerpX * roadHalfWidthPixels;
    const edgeRightY = closestInfo.closestY + screenPerpY * roadHalfWidthPixels;
    const edgeLeftX = closestInfo.closestX - screenPerpX * roadHalfWidthPixels;
    const edgeLeftY = closestInfo.closestY - screenPerpY * roadHalfWidthPixels;
    
    const distToRightEdge = Math.sqrt(Math.pow(dropPoint.x - edgeRightX, 2) + Math.pow(dropPoint.y - edgeRightY, 2));
    const distToLeftEdge = Math.sqrt(Math.pow(dropPoint.x - edgeLeftX, 2) + Math.pow(dropPoint.y - edgeLeftY, 2));
    
    // Determine snap target: centerline or edge
    // If within 40% of road half-width from centerline, snap to centerline
    const centerlineThreshold = roadHalfWidthPixels * 0.4;
    
    let snapTarget: 'centerline' | 'left-edge' | 'right-edge';
    if (distToCenterline < centerlineThreshold) {
      snapTarget = 'centerline';
    } else if (distToRightEdge < distToLeftEdge) {
      snapTarget = 'right-edge';
    } else {
      snapTarget = 'left-edge';
    }
    
    // Calculate snapped position based on target
    // Use screen-space edge positions and unproject to get accurate lat/lng
    let snappedLngLat: { lng: number; lat: number };
    
    if (snapTarget === 'centerline') {
      // Snap to centerline - use the closest point on road data
      snappedLngLat = {
        lng: closestLngLat.lng,
        lat: closestLngLat.lat
      };
    } else if (snapTarget === 'right-edge') {
      // Snap to right edge - unproject the calculated edge position
      const edgePoint = map.unproject([edgeRightX, edgeRightY]);
      snappedLngLat = {
        lng: edgePoint.lng,
        lat: edgePoint.lat
      };
    } else {
      // Snap to left edge - unproject the calculated edge position
      const edgePoint = map.unproject([edgeLeftX, edgeLeftY]);
      snappedLngLat = {
        lng: edgePoint.lng,
        lat: edgePoint.lat
      };
    }
    
    // Calculate sign rotation to face traffic
    // Signs should face TOWARD oncoming traffic so drivers can see them as they approach
    
    // Australia drives on the LEFT side of the road
    const drivesOnLeft = true; // Australia, UK, Japan, etc.
    
    // Calculate the angle of the road segment using SCREEN coordinates for visual rotation
    // (screen coords: Y increases downward, so we negate Y for standard math)
    const screenSegmentDx = closestSegment.x2 - closestSegment.x1;
    const screenSegmentDy = closestSegment.y2 - closestSegment.y1;
    let roadAngle = Math.atan2(-screenSegmentDy, screenSegmentDx) * (180 / Math.PI);
    
    // Determine sign facing angle based on snap target
    let signFacingAngle: number;
    
    if (snapTarget === 'centerline') {
      // On centerline: face perpendicular to road (toward the side where drop was closer)
      // Or for one-way roads, face against traffic
      if (isOneway) {
        signFacingAngle = roadAngle;
      } else {
        // Two-way: face the side the user dropped closer to
        signFacingAngle = side === 'left' ? roadAngle + 90 : roadAngle - 90;
      }
    } else if (isOneway) {
      // One-way edge: face toward oncoming traffic
      signFacingAngle = roadAngle;
    } else {
      // Two-way edge: face toward traffic on that side
      if (drivesOnLeft) {
        // Left-hand traffic (Australia):
        if (snapTarget === 'left-edge') {
          signFacingAngle = roadAngle; // Face with line direction
        } else {
          signFacingAngle = roadAngle + 180; // Face against line direction
        }
      } else {
        // Right-hand traffic (USA, Europe):
        if (snapTarget === 'right-edge') {
          signFacingAngle = roadAngle;
        } else {
          signFacingAngle = roadAngle + 180;
        }
      }
    }
    
    let signRotation = signFacingAngle;
    
    // Normalize to 0-360
    signRotation = ((signRotation % 360) + 360) % 360;
    
    // Convert from mathematical angle to map bearing
    // Map bearing: 0 = north, 90 = east (clockwise)
    // Math angle: 0 = east, 90 = north (counter-clockwise)
    // Conversion: bearing = 90 - mathAngle
    let bearing = 90 - signRotation;
    bearing = ((bearing % 360) + 360) % 360;
    
    // Generate a unique road segment ID from feature properties
    const featureId = closestFeature.id !== undefined 
      ? String(closestFeature.id) 
      : `${closestFeature.properties?.['class'] || 'road'}_${closestInfo.closestX.toFixed(0)}_${closestInfo.closestY.toFixed(0)}`;
    
    // Determine final side based on snap target
    const finalSide: 'left' | 'right' = snapTarget === 'left-edge' ? 'left' : 
                                        snapTarget === 'right-edge' ? 'right' : side;
    
    return {
      lat: snappedLngLat.lat,
      lng: snappedLngLat.lng,
      rotation: Math.round(bearing),
      edgeMetadata: {
        roadSegmentId: featureId,
        side: finalSide,
        positionAlongEdge: closestInfo.t, // 0-1 parameter along the segment
        edgeAngle: Math.round(roadAngle),
      }
    };
  }, []);

  // Handle drop for sign placement
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!mapRef.current || !onSignAdd || !isManualMode) {
      return;
    }

    let signCode = e.dataTransfer.getData('signCode');
    let composite: any = null;
    
    const json = e.dataTransfer.getData('application/json');
    if (json) {
      try {
        const data = JSON.parse(json);
        if (data.type === 'composite' && data.composite) {
          signCode = 'COMPOSITE';
          composite = data.composite;
        } else if (data && data.code) {
          signCode = data.code;
        }
      } catch (err) {
        console.error('Failed to parse drop data:', err);
      }
    }

    if (!signCode) return;

    const map = mapRef.current.getMap();
    const mapContainer = map.getContainer();
    const rect = mapContainer.getBoundingClientRect();
    
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const lngLat = map.unproject([x, y]);
    
    // Snap to road edge if showRoadMarkings is enabled
    let finalPosition: SnapResult;
    
    if (showRoadMarkings) {
      finalPosition = snapToRoadEdge(map, { x, y }, { lng: lngLat.lng, lat: lngLat.lat });
    } else {
      finalPosition = { 
        lat: lngLat.lat, 
        lng: lngLat.lng, 
        rotation: 0,
        edgeMetadata: {
          roadSegmentId: '',
          side: 'right',
          positionAlongEdge: 0,
          edgeAngle: 0,
        }
      };
    }

    if (composite) {
      onSignAdd(signCode, { lat: finalPosition.lat, lng: finalPosition.lng }, finalPosition.rotation, composite, finalPosition.edgeMetadata);
    } else {
      onSignAdd(signCode, { lat: finalPosition.lat, lng: finalPosition.lng }, finalPosition.rotation, undefined, finalPosition.edgeMetadata);
    }
    
    setIsDraggedOver(false);
  }, [onSignAdd, isManualMode, showRoadMarkings, snapToRoadEdge]);

  // Handle sign dot drag (moves actual sign position) - NO snapping on move, only on initial placement
  const handleSignDragEnd = useCallback((signId: string, event: MarkerDragEvent) => {
    if (!onSignMove) return;
    
    // Move sign to exact dragged position without snapping
    onSignMove(signId, { lat: event.lngLat.lat, lng: event.lngLat.lng });
  }, [onSignMove]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer && isManualMode) {
      e.dataTransfer.dropEffect = 'copy';
    } else if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'none';
    }
  }, [isManualMode]);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isManualMode) {
      setIsDraggedOver(true);
    }
  }, [isManualMode]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === e.target) {
      setIsDraggedOver(false);
    }
  }, []);

  // Helper to escape regex special characters
  const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // Helper to apply unique IDs and CSS classes to SVG elements (prevents style conflicts)
  const applyUniqueIds = useCallback((svgElement: SVGSVGElement, prefix: string) => {
    // Use plain objects instead of Map (Map import from react-map-gl shadows global Map)
    const idMap: Record<string, string> = {};
    const classMap: Record<string, string> = {};

    // Make IDs unique
    svgElement.querySelectorAll('[id]').forEach(el => {
      const oldId = el.getAttribute('id');
      if (!oldId) return;
      const newId = `${prefix}-${oldId}`;
      if (oldId === newId) return;
      idMap[oldId] = newId;
      el.setAttribute('id', newId);
    });

    // Make CSS class names unique
    svgElement.querySelectorAll('[class]').forEach(el => {
      const classList = el.getAttribute('class');
      if (!classList) return;
      const classes = classList.split(/\s+/).filter(c => c);
      const newClasses = classes.map(oldClass => {
        if (!classMap[oldClass]) {
          classMap[oldClass] = `${prefix}-${oldClass}`;
        }
        return classMap[oldClass];
      });
      el.setAttribute('class', newClasses.join(' '));
    });

    const updateValue = (value: string | null) => {
      if (!value) return value;
      let updated = value;
      Object.entries(idMap).forEach(([oldId, newId]) => {
        const oldHash = `#${oldId}`;
        const newHash = `#${newId}`;
        const urlRegex = new RegExp(`url\\(${escapeRegExp(oldHash)}\\)`, 'g');
        updated = updated.replace(urlRegex, `url(${newHash})`);
        const hashRegex = new RegExp(escapeRegExp(oldHash), 'g');
        updated = updated.replace(hashRegex, newHash);
      });
      return updated;
    };

    const attributeNames = [
      'fill', 'stroke', 'filter', 'clip-path', 'mask',
      'marker-start', 'marker-mid', 'marker-end', 'style', 'xlink:href', 'href',
    ];

    svgElement.querySelectorAll('*').forEach(el => {
      attributeNames.forEach(name => {
        if (!el.hasAttribute(name)) return;
        const value = el.getAttribute(name);
        const updated = updateValue(value);
        if (updated !== value && updated != null) {
          el.setAttribute(name, updated);
        }
      });
    });

    // Update CSS class selectors in <style> tags
    svgElement.querySelectorAll('style').forEach(styleEl => {
      const cssText = styleEl.textContent;
      if (!cssText) return;
      let updatedCss = cssText;
      Object.entries(idMap).forEach(([oldId, newId]) => {
        const oldHash = `#${oldId}`;
        const newHash = `#${newId}`;
        const regex = new RegExp(escapeRegExp(oldHash), 'g');
        updatedCss = updatedCss.replace(regex, newHash);
      });
      Object.entries(classMap).forEach(([oldClass, newClass]) => {
        const regex = new RegExp(`\\.${escapeRegExp(oldClass)}(?![a-zA-Z0-9_-])`, 'g');
        updatedCss = updatedCss.replace(regex, `.${newClass}`);
      });
      styleEl.textContent = updatedCss;
    });
  }, []);

  // Generate composite sign icon URL
  const getCompositeSignIconUrl = useCallback((composite: any, scale: number = 1.0, rotation: number = 0) => {
    if (!composite || !composite.components || !signLibrary) {
      return null;
    }

    try {
      const containerWidth = composite.canvasWidth || 200;
      const containerHeight = composite.canvasHeight || 200;

      // Build composite SVG using DOM for proper color preservation
      const containerSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      containerSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      containerSvg.setAttribute('width', String(containerWidth));
      containerSvg.setAttribute('height', String(containerHeight));
      containerSvg.setAttribute('viewBox', `0 0 ${containerWidth} ${containerHeight}`);
      
      // Add background/frame
      const bgRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      bgRect.setAttribute('x', '0');
      bgRect.setAttribute('y', '0');
      bgRect.setAttribute('width', String(containerWidth));
      bgRect.setAttribute('height', String(containerHeight));
      bgRect.setAttribute('fill', '#f5f5f5');
      bgRect.setAttribute('stroke', '#333');
      bgRect.setAttribute('stroke-width', '4');
      containerSvg.appendChild(bgRect);

      // Add each component sign
      composite.components.forEach((component: any, index: number) => {
        const signData = signLibrary?.[component.signCode.toLowerCase()];
        if (!signData?.svg) {
          console.warn(`Sign ${component.signCode} not found in library`);
          return;
        }

        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(signData.svg, 'image/svg+xml');
        const svgElement = svgDoc.querySelector('svg');

        if (svgElement) {
          // Apply unique IDs and CSS classes to prevent conflicts
          const uniquePrefix = `mapbox-${composite.id || 'comp'}-${component.id || component.signCode}-${index}`;
          applyUniqueIds(svgElement, uniquePrefix);

          const viewBox = svgElement.getAttribute('viewBox') || '0 0 40 40';
          const [vbX, vbY, vbWidth, vbHeight] = viewBox.split(' ').map(Number);
          
          // Calculate scale to fit component size
          const scaleX = component.width / vbWidth;
          const scaleY = component.height / vbHeight;
          
          // Build transform with translation and scaling
          const transform = `translate(${component.x}, ${component.y}) scale(${scaleX}, ${scaleY}) translate(${-vbX}, ${-vbY})`;
          
          // Create a group with transform
          const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
          g.setAttribute('transform', transform);
          
          // Separate style/defs from graphical elements
          const children = Array.from(svgElement.children);
          for (const child of children) {
            const clonedChild = child.cloneNode(true) as Element;
            const tagName = clonedChild.tagName.toLowerCase();
            
            // Style and defs go to the root SVG, not in the transformed group
            if (tagName === 'style' || tagName === 'defs') {
              containerSvg.appendChild(clonedChild);
            } else {
              // Graphical elements go in the transformed group
              g.appendChild(clonedChild);
            }
          }
          
          // Only append the group if it has children
          if (g.children.length > 0) {
            containerSvg.appendChild(g);
          }
        }
      });

      // Serialize the DOM element to string
      const serializer = new XMLSerializer();
      let compositeSvg = serializer.serializeToString(containerSvg);

      // Apply rotation if needed
      if (rotation !== 0) {
        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(compositeSvg, 'image/svg+xml');
        const svgElement = svgDoc.querySelector('svg');

        if (svgElement) {
          // Make viewBox square using longest side × 2 to prevent clipping during rotation
          const maxSide = Math.max(containerWidth, containerHeight) * 2;
          const centerX = containerWidth / 2;
          const centerY = containerHeight / 2;
          const newVbX = centerX - maxSide / 2;
          const newVbY = centerY - maxSide / 2;

          const existingContent = Array.from(svgElement.children);
          const g = svgDoc.createElementNS('http://www.w3.org/2000/svg', 'g');
          g.setAttribute('transform', `rotate(${rotation} ${centerX} ${centerY})`);

          existingContent.forEach(child => {
            g.appendChild(child.cloneNode(true));
          });

          while (svgElement.firstChild) {
            svgElement.removeChild(svgElement.firstChild);
          }
          svgElement.appendChild(g);

          // Set square viewBox using longest side
          svgElement.setAttribute('viewBox', `${newVbX} ${newVbY} ${maxSide} ${maxSide}`);
          svgElement.setAttribute('width', `${maxSide}`);
          svgElement.setAttribute('height', `${maxSide}`);

          compositeSvg = new XMLSerializer().serializeToString(svgElement);
        }
      }

      return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(compositeSvg)}`;
    } catch (error) {
      console.error('Error creating composite icon:', error);
      return null;
    }
  }, [signLibrary, applyUniqueIds]);

  // Generate sign icon URL
  const getSignIconUrl = useCallback((signCode: string, scale: number = 1.0, rotation: number = 0) => {
    if (!signLibrary || !signCode) {
      return null;
    }

    const normalizedSignCode = signCode.toLowerCase();
    const signData = signLibrary[normalizedSignCode] || signLibrary[signCode];
    
    if (!signData?.svg) {
      return null;
    }

    try {
      let svg = signData.svg;
      
      // Parse SVG to apply rotation
      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(svg, 'image/svg+xml');
      const svgElement = svgDoc.querySelector('svg');
      
      if (svgElement && rotation !== 0) {
        // Get original viewBox to find center
        let viewBoxAttr = svgElement.getAttribute('viewBox');
        let vbX = 0, vbY = 0, vbWidth = 40, vbHeight = 40;
        
        if (viewBoxAttr) {
          [vbX, vbY, vbWidth, vbHeight] = viewBoxAttr.split(/\s+/).map(Number);
        } else {
          const widthAttr = svgElement.getAttribute('width');
          const heightAttr = svgElement.getAttribute('height');
          if (widthAttr) vbWidth = parseFloat(widthAttr);
          if (heightAttr) vbHeight = parseFloat(heightAttr);
        }
        
        // Make viewBox square using longest side × 2 to prevent clipping during rotation
        // When rotated 45°, corners extend to ~1.41× original size, so 2× gives safe margin
        const maxSide = Math.max(vbWidth, vbHeight) * 2;
        const centerX = vbX + vbWidth / 2;
        const centerY = vbY + vbHeight / 2;
        const newVbX = centerX - maxSide / 2;
        const newVbY = centerY - maxSide / 2;
        
        // Wrap existing content in a group with rotation transform around center
        const existingContent = Array.from(svgElement.children);
        const g = svgDoc.createElementNS('http://www.w3.org/2000/svg', 'g');
        g.setAttribute('transform', `rotate(${rotation} ${centerX} ${centerY})`);
        
        existingContent.forEach(child => {
          g.appendChild(child.cloneNode(true));
        });
        
        while (svgElement.firstChild) {
          svgElement.removeChild(svgElement.firstChild);
        }
        svgElement.appendChild(g);
        
        // Set square viewBox using longest side
        svgElement.setAttribute('viewBox', `${newVbX} ${newVbY} ${maxSide} ${maxSide}`);
        svgElement.setAttribute('width', `${maxSide}`);
        svgElement.setAttribute('height', `${maxSide}`);
        
        svg = new XMLSerializer().serializeToString(svgElement);
      }
      
      return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
    } catch (error) {
      console.error('Error creating SVG icon:', error);
      return null;
    }
  }, [signLibrary]);

  // Create GeoJSON for workzone polygons
  const workzoneGeoJSON = {
    type: 'FeatureCollection' as const,
    features: workzones
      .filter(wz => wz.polygon && wz.polygon.length >= 3)
      .map((wz, idx) => ({
        type: 'Feature' as const,
        properties: { id: wz.id || idx },
        geometry: {
          type: 'Polygon' as const,
          coordinates: [[
            ...wz.polygon.map((coord: any) => [coord.lng, coord.lat]),
            [wz.polygon[0].lng, wz.polygon[0].lat] // Close the polygon
          ]],
        },
      })),
  };

  // Create GeoJSON for drawing preview polygon
  const drawingPreviewGeoJSON = drawingPoints.length >= 2 ? {
    type: 'Feature' as const,
    properties: {},
    geometry: {
      type: 'LineString' as const,
      coordinates: drawingPoints.map(p => [p.lng, p.lat]),
    },
  } : null;

  // Create GeoJSON for temp arrow
  const tempArrowGeoJSON = arrowDraft?.start && tempArrowEnd ? {
    type: 'Feature' as const,
    properties: {},
    geometry: {
      type: 'LineString' as const,
      coordinates: [
        [arrowDraft.start.lng, arrowDraft.start.lat],
        [tempArrowEnd.lng, tempArrowEnd.lat],
      ],
    },
  } : null;

  // Map of sign IDs to sign data for looking up edge metadata
  const signById = useMemo(() => {
    const map = new globalThis.Map();
    (signPlacement?.signs || []).forEach((sign: any) => {
      map.set(sign.id, sign);
    });
    return map;
  }, [signPlacement?.signs]);

  // Helper to get offset direction for an arrow based on linked signs
  const getArrowOffset = (arrow: ArrowDescriptor, offsetMeters: number) => {
    const bearing = bearingDegrees(arrow.start, arrow.end);
    const startSign = arrow.startSignId ? signById.get(arrow.startSignId) : null;
    const endSign = arrow.endSignId ? signById.get(arrow.endSignId) : null;
    const side = startSign?.edgeMetadata?.side || endSign?.edgeMetadata?.side;
    
    // Offset perpendicular to the arrow direction
    // Left side of road = offset to the right of the arrow direction (away from road)
    // Right side of road = offset to the left of the arrow direction (away from road)
    const perpBearing = side === 'left' 
      ? (bearing + 90) % 360   // Offset right
      : side === 'right'
        ? (bearing + 270) % 360  // Offset left
        : null;
    
    if (perpBearing === null) {
      return { start: arrow.start, end: arrow.end, mid: { lat: (arrow.start.lat + arrow.end.lat) / 2, lng: (arrow.start.lng + arrow.end.lng) / 2 } };
    }
    
    return {
      start: offsetLatLng(arrow.start, perpBearing, offsetMeters),
      end: offsetLatLng(arrow.end, perpBearing, offsetMeters),
      mid: offsetLatLng({ lat: (arrow.start.lat + arrow.end.lat) / 2, lng: (arrow.start.lng + arrow.end.lng) / 2 }, perpBearing, offsetMeters),
    };
  };

  const arrowLineGeoJSON = useMemo(() => ({
    type: 'FeatureCollection' as const,
    features: arrows.map((arrow) => {
      // Apply drag offset if this arrow is being dragged
      const isDragging = draggingArrow?.arrowId === arrow.id && arrowDragOffset;
      const adjustedArrow = isDragging ? {
        ...arrow,
        start: { lat: arrow.start.lat + arrowDragOffset!.lat, lng: arrow.start.lng + arrowDragOffset!.lng },
        end: { lat: arrow.end.lat + arrowDragOffset!.lat, lng: arrow.end.lng + arrowDragOffset!.lng },
      } : arrow;
      
      const offset = getArrowOffset(adjustedArrow, 5); // 5 meter offset
      return {
        type: 'Feature' as const,
        properties: { id: arrow.id, arrowId: arrow.id },
        geometry: {
          type: 'LineString' as const,
          coordinates: [
            [offset.start.lng, offset.start.lat],
            [offset.end.lng, offset.end.lat],
          ],
        },
      };
    }),
  }), [arrows, signById, draggingArrow, arrowDragOffset]);

  const arrowHeadGeoJSON = useMemo(() => ({
    type: 'FeatureCollection' as const,
    features: arrows.flatMap((arrow) => {
      // Apply drag offset if this arrow is being dragged
      const isDragging = draggingArrow?.arrowId === arrow.id && arrowDragOffset;
      const adjustedArrow = isDragging ? {
        ...arrow,
        start: { lat: arrow.start.lat + arrowDragOffset!.lat, lng: arrow.start.lng + arrowDragOffset!.lng },
        end: { lat: arrow.end.lat + arrowDragOffset!.lat, lng: arrow.end.lng + arrowDragOffset!.lng },
      } : arrow;
      
      const bearing = bearingDegrees(adjustedArrow.start, adjustedArrow.end);
      const offset = getArrowOffset(adjustedArrow, 5);
      return [
        {
          type: 'Feature' as const,
          properties: { id: `${arrow.id}-start`, arrowId: arrow.id, bearing: (bearing + 180) % 360 },
          geometry: {
            type: 'Point' as const,
            coordinates: [offset.start.lng, offset.start.lat],
          },
        },
        {
          type: 'Feature' as const,
          properties: { id: `${arrow.id}-end`, arrowId: arrow.id, bearing },
          geometry: {
            type: 'Point' as const,
            coordinates: [offset.end.lng, offset.end.lat],
          },
        },
      ];
    }),
  }), [arrows, signById, draggingArrow, arrowDragOffset]);

  const arrowBarGeoJSON = useMemo(() => ({
    type: 'FeatureCollection' as const,
    features: arrows.map((arrow) => {
      // Apply drag offset if this arrow is being dragged
      const isDragging = draggingArrow?.arrowId === arrow.id && arrowDragOffset;
      const adjustedArrow = isDragging ? {
        ...arrow,
        start: { lat: arrow.start.lat + arrowDragOffset!.lat, lng: arrow.start.lng + arrowDragOffset!.lng },
        end: { lat: arrow.end.lat + arrowDragOffset!.lat, lng: arrow.end.lng + arrowDragOffset!.lng },
      } : arrow;
      
      const bearing = bearingDegrees(adjustedArrow.start, adjustedArrow.end);
      const offset = getArrowOffset(adjustedArrow, 5);
      return {
        type: 'Feature' as const,
        properties: { id: `${arrow.id}-start`, arrowId: arrow.id, bearing: (bearing + 90) % 360 },
        geometry: {
          type: 'Point' as const,
          coordinates: [offset.start.lng, offset.start.lat],
        },
      };
    }),
  }), [arrows, signById, draggingArrow, arrowDragOffset]);

  const arrowLabelGeoJSON = useMemo(() => ({
    type: 'FeatureCollection' as const,
    features: arrows
      .filter((arrow) => arrow.showDistanceLabel !== false)
      .map((arrow) => {
        // Apply drag offset if this arrow is being dragged
        const isDragging = draggingArrow?.arrowId === arrow.id && arrowDragOffset;
        const adjustedArrow = isDragging ? {
          ...arrow,
          start: { lat: arrow.start.lat + arrowDragOffset!.lat, lng: arrow.start.lng + arrowDragOffset!.lng },
          end: { lat: arrow.end.lat + arrowDragOffset!.lat, lng: arrow.end.lng + arrowDragOffset!.lng },
        } : arrow;
        
        const offset = getArrowOffset(adjustedArrow, 5);
        return {
          type: 'Feature' as const,
          properties: {
            id: `${arrow.id}-label`,
            arrowId: arrow.id,
            label: `${formatDistance(arrow.distance)} m`,
          },
          geometry: {
            type: 'Point' as const,
            coordinates: [offset.mid.lng, offset.mid.lat],
          },
        };
      }),
  }), [arrows, formatDistance, signById, draggingArrow, arrowDragOffset]);

  // Create GeoJSON for delineator lines (the paths)
  const delineatorLinesGeoJSON = {
    type: 'FeatureCollection' as const,
    features: delineatorLines.map(line => ({
      type: 'Feature' as const,
      properties: { id: line.id, deviceType: line.deviceType, spacing: line.spacing },
      geometry: {
        type: 'LineString' as const,
        coordinates: line.path.map(p => [p.lng, p.lat]),
      },
    })),
  };

  // Calculate device positions for all delineator lines
  const delineatorDevicePositions = delineatorLines.flatMap(line => {
    const positions = calculateDelineatorPositions(line.path, line.spacing);
    return positions.map((pos, idx) => ({
      ...pos,
      lineId: line.id,
      deviceType: line.deviceType,
      deviceSize: line.deviceSize,
      index: idx,
      isFirst: idx === 0, // Mark first device for drag handle
      originalPath: line.path, // Store original path for drag calculations
    }));
  });

  // Fixed base size for delineator devices (same as signs)
  const delineatorBaseSize = 16;

  // Create GeoJSON for delineator draft preview (the line being drawn)
  const delineatorDraftGeoJSON = delineatorDraft && delineatorDraft.path.length > 0 ? {
    type: 'Feature' as const,
    properties: {},
    geometry: {
      type: 'LineString' as const,
      coordinates: [
        ...delineatorDraft.path.map(p => [p.lng, p.lat]),
        ...(tempDelineatorEnd ? [[tempDelineatorEnd.lng, tempDelineatorEnd.lat]] : []),
      ],
    },
  } : null;

  // Calculate device positions for draft preview
  const delineatorDraftPositions = delineatorDraft && delineatorDraft.path.length >= 2 
    ? calculateDelineatorPositions(delineatorDraft.path, delineatorDraft.spacing)
    : [];

  // Create GeoJSON for road tool overlay
  const roadToolGeoJSON = roadToolGeometry && roadToolVisible && isProMode ? roadToolGeometry : null;

  // Create GeoJSON for persistent print area bounding box
  const printAreaGeoJSON = printArea ? {
    type: 'Feature' as const,
    properties: {},
    geometry: {
      type: 'Polygon' as const,
      coordinates: [[
        [printArea.southwest.lng, printArea.southwest.lat],
        [printArea.northeast.lng, printArea.southwest.lat],
        [printArea.northeast.lng, printArea.northeast.lat],
        [printArea.southwest.lng, printArea.northeast.lat],
        [printArea.southwest.lng, printArea.southwest.lat],
      ]],
    },
  } : null;

  if (!apiKey) {
    return (
      <Paper elevation={3} sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1">Loading map...</Typography>
      </Paper>
    );
  }

  return (
    <Paper
      ref={mapContainerRef}
      elevation={3}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        ...(isManualMode && isDraggedOver && {
          backgroundColor: 'rgba(76, 175, 80, 0.1)',
          '&::before': {
            content: '"Drop sign here"',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'rgba(76, 175, 80, 0.9)',
            color: 'white',
            padding: '8px 16px',
            borderRadius: '4px',
            fontSize: '16px',
            fontWeight: 'bold',
            zIndex: 10000,
            pointerEvents: 'none',
          },
        }),
      }}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onMouseMove={handleOverlayMouseMove}
      onMouseUp={handleOverlayMouseUp}
      onMouseLeave={handleOverlayMouseUp}
    >
      {/* Road Overlay Toggle - Top Right (left of user controls) */}
      <Paper
        elevation={3}
        onClick={(e) => {
          const newShowState = !showRoadMarkings;
          setShowRoadMarkings(newShowState);
          // Update settings
          handleRoadOverlaySettingsChange({ enabled: newShowState });
          // Select road overlay in properties panel when enabled
          if (newShowState) {
            onElementSelect?.({ type: 'roadoverlay', data: { ...roadOverlaySettings, enabled: newShowState } });
          }
        }}
        onContextMenu={(e) => {
          // Right-click opens properties without toggling
          e.preventDefault();
          onElementSelect?.({ type: 'roadoverlay', data: roadOverlaySettings });
        }}
        sx={{
          position: 'absolute',
          top: 32,
          right: 200,
          zIndex: 20,
          display: 'flex',
          alignItems: 'center',
          height: 48,
          px: 2,
          gap: 1,
          borderRadius: 999,
          backgroundColor: showRoadMarkings ? '#1976d2' : '#FFFFFF',
          color: showRoadMarkings ? '#FFFFFF' : '#111827',
          boxShadow: '0px 18px 40px rgba(15, 23, 42, 0.22)',
          cursor: 'pointer',
          userSelect: 'none',
          transition: 'all 0.2s',
          '&:hover': {
            backgroundColor: showRoadMarkings ? '#1565c0' : '#F5F5F5',
          },
        }}
      >
        
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
          Road Overlay
        </Typography>
      </Paper>

      {/* Search Bar - matching Google Maps styling */}
      <Paper
        elevation={3}
        sx={{
          position: 'absolute',
          top: 32,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 20,
          pointerEvents: isDrawingPrintArea ? 'none' : 'auto',
          width: 420,
          maxWidth: '90vw',
          borderRadius: 999,
          display: 'flex',
          alignItems: 'center',
          gap: 2.5,
          boxShadow: '0px 18px 40px rgba(15, 23, 42, 0.22)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 0.5,
            }}
          >
            <img
              src={logo}
              alt="Logo"
              style={{
                width: '80%',
                height: '80%',
                objectFit: 'cover',
              }}
            />
          </Box>
        </Box>

        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
          <Box sx={{ width: '100%' }}>
            <InputBase
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSearch();
                }
              }}
              placeholder="Enter your worksite address"
              fullWidth
              sx={{
                flex: 1,
                width: '100%',
                fontSize: '1rem',
                fontWeight: 400,
                color: '#111827',
                '&::placeholder': {
                  color: '#9ca3af',
                },
              }}
            />
          </Box>
        </Box>

        <IconButton
          onClick={handleSearch}
          sx={{
            width: 48,
            height: 48,
            backgroundColor: 'transparent',
            color: 'black',
            '&:hover': { backgroundColor: 'transparent' },
          }}
        >
          <img
            src="/images/search-alt-svgrepo-com.svg"
            alt="Search"
            style={{ width: 27, height: 27 }}
          />
        </IconButton>
      </Paper>

      {/* Screen-Space Bounding Box Overlay for Print Area Selection */}
      {boundingBoxOverlay && isDrawingPrintArea && (
        <Box
          data-export-ignore="true"  // Ignore this overlay during canvas capture
          sx={{
            position: 'absolute',
            left: boundingBoxOverlay.x,
            top: boundingBoxOverlay.y,
            width: boundingBoxOverlay.width,
            height: boundingBoxOverlay.height,
            border: '3px dashed #FF0000',
            backgroundColor: 'rgba(255, 0, 0, 0.15)',
            zIndex: 1000,
            cursor: isDraggingOverlay ? 'grabbing' : 'grab',
            boxSizing: 'border-box',
          }}
          onMouseDown={handleOverlayDragStart}
        >
          {/* Control buttons */}
          <Box
            sx={{
              position: 'absolute',
              top: -30,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 2.5,
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleConfirmBoundingBox}
              style={{
                padding: '4px 12px',
                backgroundColor: 'rgb(31, 208, 37)',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px',
              }}
            >
              Confirm
            </button>
            <button
              onClick={handleCancelBoundingBox}
              style={{
                padding: '4px 12px',
                backgroundColor: 'rgb(244, 67, 54)',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px',
              }}
            >
              Cancel
            </button>
          </Box>
          
          {/* Size indicator - clickable to change paper size */}
          <Box
            onClick={(e) => {
              e.stopPropagation();
              setPaperSizeMenuAnchor(paperSizeMenuAnchor ? null : e.currentTarget);
            }}
            sx={{
              position: 'absolute',
              bottom: -30,
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'rgba(0,0,0,0.7)',
              color: 'white',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              whiteSpace: 'nowrap',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.85)',
              },
            }}
          >
            {selectedPaperLabel}
          </Box>
          
          {/* Paper size selection popup */}
          {paperSizeMenuAnchor && (
            <Box
              onClick={(e) => e.stopPropagation()}
              sx={{
                position: 'absolute',
                bottom: -125,
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'rgba(0,0,0,0.85)',
                borderRadius: '4px',
                padding: '8px',
                zIndex: 1001,
              }}
            >
              {/* Orientation toggle buttons */}
              <Box sx={{ display: 'flex', gap: '4px', mb: 1 }}>
                <button
                  onClick={() => {
                    const currentSize = selectedPaperLabel.split(' ')[0] as PaperSize;
                    const option = PAPER_OPTIONS.find(p => p.size === currentSize && p.orientation === 'landscape');
                    if (option) {
                      setSelectedPaperLabel(option.label);
                      onPaperSizeChange?.(option.pixelWidth, option.pixelHeight, option.label);
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: '4px 8px',
                    backgroundColor: selectedPaperLabel.includes('Landscape') ? '#4CAF50' : 'transparent',
                    color: 'white',
                    border: '1px solid rgba(255,255,255,0.3)',
                    borderRadius: '3px',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: selectedPaperLabel.includes('Landscape') ? 'bold' : 'normal',
                  }}
                >
                  Landscape
                </button>
                <button
                  onClick={() => {
                    const currentSize = selectedPaperLabel.split(' ')[0] as PaperSize;
                    const option = PAPER_OPTIONS.find(p => p.size === currentSize && p.orientation === 'portrait');
                    if (option) {
                      setSelectedPaperLabel(option.label);
                      onPaperSizeChange?.(option.pixelWidth, option.pixelHeight, option.label);
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: '4px 8px',
                    backgroundColor: selectedPaperLabel.includes('Portrait') ? '#4CAF50' : 'transparent',
                    color: 'white',
                    border: '1px solid rgba(255,255,255,0.3)',
                    borderRadius: '3px',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: selectedPaperLabel.includes('Portrait') ? 'bold' : 'normal',
                  }}
                >
                  Portrait
                </button>
              </Box>
              
              {/* Paper size options */}
              <Box sx={{ display: 'flex', gap: '4px' }}>
                {(['A4', 'A3', 'A2', 'A1', 'A0'] as PaperSize[]).map((size) => {
                  const currentOrientation = selectedPaperLabel.includes('Portrait') ? 'portrait' : 'landscape';
                  const option = PAPER_OPTIONS.find(p => p.size === size && p.orientation === currentOrientation);
                  const isSelected = selectedPaperLabel.startsWith(size);
                  return (
                    <button
                      key={size}
                      onClick={() => {
                        if (option) {
                          setSelectedPaperLabel(option.label);
                          onPaperSizeChange?.(option.pixelWidth, option.pixelHeight, option.label);
                          setPaperSizeMenuAnchor(null);
                        }
                      }}
                      style={{
                        padding: '6px 10px',
                        backgroundColor: isSelected ? '#4CAF50' : 'transparent',
                        color: 'white',
                        border: '1px solid rgba(255,255,255,0.3)',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: isSelected ? 'bold' : 'normal',
                      }}
                    >
                      {size}
                    </button>
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Mapbox Map */}
      <Box sx={{ flexGrow: 1, height: '100%', position: 'relative' }}>
        <Map
          ref={mapRef}
          {...viewState}
          onMove={(evt: ViewStateChangeEvent) => setViewState(evt.viewState)}
          mapboxAccessToken={apiKey}
          mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
          maxZoom={22}
          minZoom={10}
          maxPitch={85}
          antialias={true}  // Smoother edges and text rendering
          onClick={handleMapClick}
          interactiveLayerIds={['arrow-hit', 'arrow-lines', 'arrow-heads', 'arrow-bars', 'arrow-labels']}
          onMouseDown={handleMapMouseDown}
          onMouseMove={handleMapMouseMove}
          onMouseUp={handleMapMouseUp}
          onLoad={(evt) => {
            const map = evt.target;
            const layersToHide = ['poi-label', 'housenum-label'];

            layersToHide.forEach(layer => {
              map.setLayoutProperty(layer, 'visibility', 'none');
            });

            ensureArrowheadImage(map);
            
            // Mark map as loaded so onMapReady can be called
            setIsMapLoaded(true);
          }}
          onDblClick={handleMapDoubleClick}
          cursor={draggingArrow ? 'move' : drawingMode ? 'crosshair' : currentTool === 'arrow' ? 'crosshair' : 'grab'}
          doubleClickZoom={!drawingMode}
          dragPan={!draggingArrow}
          preserveDrawingBuffer={true}  // Required for canvas capture/export
        >
          {/* Combined control panel - bottom right */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 30,
              right: 10,
              zIndex: 10,
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}
          >
            {/* Map Fade slider - always visible */}
            <Box
              sx={{
                backgroundColor: 'white',
                borderRadius: '4px',
                boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                padding: '6px 10px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '12px',
              }}
            >
              <span>Map Fade</span>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={mapFadeOpacity}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  setMapFadeOpacity(val);
                  onMapFadeChange?.(val);
                }}
                style={{ width: '80px', cursor: 'pointer', accentColor: '#666' }}
              />
              <span style={{ width: '30px' }}>{Math.round(mapFadeOpacity * 100)}%</span>
            </Box>

            {/* Heading/Tilt + Zoom controls row */}
            <Box sx={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
              {/* Heading/Tilt panel */}
              <Box
                sx={{
                  width: 140,
                  backgroundColor: 'white',
                  borderRadius: '4px',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                  padding: '6px 8px',
                  fontSize: '11px',
                }}
              >
                {/* Current values */}
                <Box sx={{ textAlign: 'center', fontWeight: 'bold', color: '#333', mb: 0.5, fontSize: '10px' }}>
                  {viewState.bearing.toFixed(0)}° | {viewState.pitch.toFixed(0)}°
                </Box>
                {/* Heading */}
                <Box sx={{ display: 'flex', gap: '2px', mb: '4px' }}>
                  <button onClick={() => setViewState(prev => ({ ...prev, bearing: (prev.bearing - 15 + 360) % 360 }))}
                    style={{ flex: 1, padding: '3px 8px', cursor: 'pointer', border: '1px solid #ccc', borderRadius: '3px', backgroundColor: '#f8f8f8', fontSize: '10px' }}>←</button>
                  <button onClick={() => setViewState(prev => ({ ...prev, bearing: 0 }))}
                    style={{ flex: 1, padding: '3px 8px', cursor: 'pointer', border: '1px solid #ccc', borderRadius: '3px', backgroundColor: '#f8f8f8', fontSize: '10px' }}>N</button>
                  <button onClick={() => setViewState(prev => ({ ...prev, bearing: (prev.bearing + 15) % 360 }))}
                    style={{ flex: 1, padding: '3px 8px', cursor: 'pointer', border: '1px solid #ccc', borderRadius: '3px', backgroundColor: '#f8f8f8', fontSize: '10px' }}>→</button>
                </Box>
                {/* Tilt */}
                <Box sx={{ display: 'flex', gap: '2px' }}>
                  <button onClick={() => setViewState(prev => ({ ...prev, pitch: Math.max(0, prev.pitch - 10) }))}
                    style={{ flex: 1, padding: '3px 8px', cursor: 'pointer', border: '1px solid #ccc', borderRadius: '3px', backgroundColor: '#f8f8f8', fontSize: '10px' }}>↓</button>
                  <button onClick={() => setViewState(prev => ({ ...prev, pitch: 0 }))}
                    style={{ flex: 1, padding: '3px 8px', cursor: 'pointer', border: '1px solid #ccc', borderRadius: '3px', backgroundColor: '#f8f8f8', fontSize: '10px' }}>0</button>
                  <button onClick={() => setViewState(prev => ({ ...prev, pitch: Math.min(85, prev.pitch + 10) }))}
                    style={{ flex: 1, padding: '3px 8px', cursor: 'pointer', border: '1px solid #ccc', borderRadius: '3px', backgroundColor: '#f8f8f8', fontSize: '10px' }}>↑</button>
                </Box>
              </Box>

              {/* Zoom controls */}
              <Box
                sx={{
                  backgroundColor: 'white',
                  borderRadius: '4px',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <button
                  onClick={() => mapRef.current?.getMap().zoomIn()}
                  style={{ padding: '8px 12px', cursor: 'pointer', border: 'none', borderBottom: '1px solid #eee', backgroundColor: 'white', borderRadius: '4px 4px 0 0', fontSize: '16px' }}
                >+</button>
                <button
                  onClick={() => mapRef.current?.getMap().zoomOut()}
                  style={{ padding: '8px 12px', cursor: 'pointer', border: 'none', backgroundColor: 'white', borderRadius: '0 0 4px 4px', fontSize: '16px' }}
                >−</button>
              </Box>

            </Box>
          </Box>
          
          {/* TGS Road Markings - styled overlay on Mapbox roads */}
          {/* 
            LAYERED APPROACH for proper intersection blending:
            Roads are rendered in THREE PHASES to ensure proper connections:
            
            PHASE 1 - ALL CASINGS (edges) in priority order:
              Tunnel → Service → Street → Secondary → Primary → Trunk → Motorway → Bridge
              
            PHASE 2 - ALL SURFACES (road fill) in priority order:
              Same order - surfaces draw ON TOP of all casings, creating natural blending
              at intersections where lower-priority road surfaces show through.
              
            PHASE 3 - ALL LANE MARKINGS (centerlines, dividers, medians) last:
              Street → Secondary → Primary → Trunk → Motorway
            
            This structure ensures that at intersections:
            - Lower-priority road surfaces remain visible where not covered by higher-priority surfaces
            - No harsh "floating" effect where major roads cross minor ones
            - Natural visual blending at all road connections
          */}
          {/* Road markings visibility logic:
              - If showRoadMarkings is off: don't show
              - If showOnlyNearSigns is on but no matching road features found: don't show
              - Otherwise: show road markings (filtering is done via setFilter)
          */}
          {showRoadMarkings && (!roadOverlaySettings.showOnlyNearSigns || signRoadFeatureIds.size > 0) && (
            <>
              {/* ============================================================ */}
              {/* INVISIBLE MARKER LAYERS - Control render order:              */}
              {/* Casings (before surfaces-base) → Surfaces (before            */}
              {/* markings-base) → Lane markings (before road-label)           */}
              {/* This ensures road surfaces always render ON TOP of casings,  */}
              {/* so intersections blend naturally instead of "floating"       */}
              {/* ============================================================ */}
              <Layer
                id="tgs-surfaces-base"
                type="line"
                source="composite"
                source-layer="road"
                beforeId="road-label"
                filter={['==', ['get', 'class'], 'NEVER_MATCH_PLACEHOLDER']}
                paint={{ 'line-opacity': 0, 'line-width': 0 }}
              />
              <Layer
                id="tgs-markings-base"
                type="line"
                source="composite"
                source-layer="road"
                beforeId="road-label"
                filter={['==', ['get', 'class'], 'NEVER_MATCH_PLACEHOLDER']}
                paint={{ 'line-opacity': 0, 'line-width': 0 }}
              />

              {/* ============================================================ */}
              {/* PHASE 1: ALL CASINGS - Draw first (below surfaces)           */}
              {/* ============================================================ */}
              {/* Tunnel Casing */}
              <Layer
                id="tgs-tunnel-casing"
                type="line"
                source="composite"
                source-layer="road"
                beforeId="tgs-surfaces-base"
                filter={['all',
                  ['==', ['get', 'structure'], 'tunnel'],
                  ['match', ['get', 'class'], 
                    ['motorway', 'motorway_link', 'trunk', 'trunk_link', 'primary', 'primary_link', 
                     'secondary', 'secondary_link', 'tertiary', 'tertiary_link', 
                     'street', 'street_limited', 'residential', 'service'], true, false
                  ]
                ]}
                layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                paint={{
                  'line-color': '#D0D0D0',
                  'line-width': ['interpolate', ['linear'], ['zoom'],
                    12, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'],
                      ['match', ['get', 'class'], 'motorway', 4, 'motorway_link', 2, 'trunk', 4, 'trunk_link', 2, 'primary', 4, 'primary_link', 2, 2]], 4.5],
                    14, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'],
                      ['match', ['get', 'class'], 'motorway', 4, 'motorway_link', 2, 'trunk', 4, 'trunk_link', 2, 'primary', 4, 'primary_link', 2, 2]], 8],
                    16, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'],
                      ['match', ['get', 'class'], 'motorway', 4, 'motorway_link', 2, 'trunk', 4, 'trunk_link', 2, 'primary', 4, 'primary_link', 2, 2]], 13.5],
                    18, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'],
                      ['match', ['get', 'class'], 'motorway', 4, 'motorway_link', 2, 'trunk', 4, 'trunk_link', 2, 'primary', 4, 'primary_link', 2, 2]], 24.5],
                    20, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'],
                      ['match', ['get', 'class'], 'motorway', 4, 'motorway_link', 2, 'trunk', 4, 'trunk_link', 2, 'primary', 4, 'primary_link', 2, 2]], 50]
                  ],
                  'line-opacity': 0.4,
                  'line-dasharray': [2, 1],
                }}
              />
              <Layer
                id="tgs-tunnel-surface"
                type="line"
                source="composite"
                source-layer="road"
                beforeId="tgs-markings-base"
                filter={['all',
                  ['==', ['get', 'structure'], 'tunnel'],
                  ['match', ['get', 'class'], 
                    ['motorway', 'motorway_link', 'trunk', 'trunk_link', 'primary', 'primary_link', 
                     'secondary', 'secondary_link', 'tertiary', 'tertiary_link', 
                     'street', 'street_limited', 'residential', 'service'], true, false
                  ]
                ]}
                layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                paint={{
                  'line-color': '#707070',
                  'line-width': ['interpolate', ['linear'], ['zoom'],
                    12, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'],
                      ['match', ['get', 'class'], 'motorway', 4, 'motorway_link', 2, 'trunk', 4, 'trunk_link', 2, 'primary', 4, 'primary_link', 2, 2]], 4],
                    14, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'],
                      ['match', ['get', 'class'], 'motorway', 4, 'motorway_link', 2, 'trunk', 4, 'trunk_link', 2, 'primary', 4, 'primary_link', 2, 2]], 7],
                    16, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'],
                      ['match', ['get', 'class'], 'motorway', 4, 'motorway_link', 2, 'trunk', 4, 'trunk_link', 2, 'primary', 4, 'primary_link', 2, 2]], 12],
                    18, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'],
                      ['match', ['get', 'class'], 'motorway', 4, 'motorway_link', 2, 'trunk', 4, 'trunk_link', 2, 'primary', 4, 'primary_link', 2, 2]], 22],
                    20, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'],
                      ['match', ['get', 'class'], 'motorway', 4, 'motorway_link', 2, 'trunk', 4, 'trunk_link', 2, 'primary', 4, 'primary_link', 2, 2]], 45]
                  ],
                  'line-opacity': 0.6,
                  'line-dasharray': [2, 1],
                }}
              />

              {/* ============================================================ */}
              {/* LAYER 0.5: SERVICE/RESIDENTIAL - Very low priority roads     */}
              {/* Parking lots, driveways, alleys, minor residential streets   */}
              {/* ============================================================ */}
              <Layer
                id="tgs-service-casing"
                type="line"
                source="composite"
                source-layer="road"
                beforeId="tgs-surfaces-base"
                filter={['all',
                  ['any',
                    ['!', ['has', 'structure']],
                    ['==', ['get', 'structure'], ''],
                    ['==', ['get', 'structure'], 'none'],
                    ['all',
                      ['!=', ['get', 'structure'], 'tunnel'],
                      ['!=', ['get', 'structure'], 'bridge']
                    ]
                  ],
                  ['match', ['get', 'class'], ['service', 'residential'], true, false]
                ]}
                layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                paint={{
                  'line-color': '#D0D0D0',
                  'line-width': ['interpolate', ['linear'], ['zoom'],
                    14, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 1], 4],
                    16, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 1], 7],
                    18, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 1], 12],
                    20, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 1], 24]
                  ],
                  'line-opacity': 1,
                }}
              />
              <Layer
                id="tgs-service-surface"
                type="line"
                source="composite"
                source-layer="road"
                beforeId="tgs-markings-base"
                filter={['all',
                  ['any',
                    ['!', ['has', 'structure']],
                    ['==', ['get', 'structure'], ''],
                    ['==', ['get', 'structure'], 'none'],
                    ['all',
                      ['!=', ['get', 'structure'], 'tunnel'],
                      ['!=', ['get', 'structure'], 'bridge']
                    ]
                  ],
                  ['match', ['get', 'class'], ['service', 'residential'], true, false]
                ]}
                layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                paint={{
                  'line-color': '#707070',
                  'line-width': ['interpolate', ['linear'], ['zoom'],
                    14, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 1], 3.4],
                    16, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 1], 6],
                    18, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 1], 10.5],
                    20, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 1], 21]
                  ],
                  'line-opacity': 1,
                }}
              />

              {/* ============================================================ */}
              {/* LAYER 1: STREET/TERTIARY - Low priority roads                */}
              {/* ============================================================ */}
              {/* Street/Tertiary Casing (white, wider) */}
              <Layer
                id="tgs-street-casing"
                type="line"
                source="composite"
                source-layer="road"
                beforeId="tgs-surfaces-base"
                filter={['all',
                  ['any',
                    ['!', ['has', 'structure']],
                    ['==', ['get', 'structure'], ''],
                    ['==', ['get', 'structure'], 'none'],
                    ['all',
                      ['!=', ['get', 'structure'], 'tunnel'],
                      ['!=', ['get', 'structure'], 'bridge']
                    ]
                  ],
                  ['match', ['get', 'class'], ['tertiary', 'tertiary_link', 'street', 'street_limited'], true, false]
                ]}
                layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                paint={{
                  'line-color': '#D0D0D0',
                  'line-width': ['interpolate', ['linear'], ['zoom'],
                    12, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 2], 4.6],
                    14, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 2], 8.2],
                    16, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 2], 14],
                    18, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 2], 25.5],
                    20, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 2], 52]
                  ],
                  'line-opacity': 1,
                }}
              />
              {/* Street/Tertiary Surface (gray, narrower - on top of casing) */}
              <Layer
                id="tgs-street-surface"
                type="line"
                source="composite"
                source-layer="road"
                beforeId="tgs-markings-base"
                filter={['all',
                  ['any',
                    ['!', ['has', 'structure']],
                    ['==', ['get', 'structure'], ''],
                    ['==', ['get', 'structure'], 'none'],
                    ['all',
                      ['!=', ['get', 'structure'], 'tunnel'],
                      ['!=', ['get', 'structure'], 'bridge']
                    ]
                  ],
                  ['match', ['get', 'class'], ['tertiary', 'tertiary_link', 'street', 'street_limited'], true, false]
                ]}
                layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                paint={{
                  'line-color': '#707070',
                  'line-width': ['interpolate', ['linear'], ['zoom'],
                    12, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 2], 4],
                    14, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 2], 7],
                    16, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 2], 12],
                    18, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 2], 22],
                    20, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 2], 45]
                  ],
                  'line-opacity': 1,
                }}
              />
              {/* Street/Tertiary Centerline - for bidirectional roads */}
              {/* Renders before secondary surface so it gets masked at intersections with major roads */}
              <Layer
                id="tgs-street-centerline"
                type="line"
                source="composite"
                source-layer="road"
                beforeId="tgs-secondary-surface"
                minzoom={13}
                filter={['all',
                  ['any',
                    ['!', ['has', 'structure']],
                    ['==', ['get', 'structure'], ''],
                    ['==', ['get', 'structure'], 'none'],
                    ['all',
                      ['!=', ['get', 'structure'], 'tunnel'],
                      ['!=', ['get', 'structure'], 'bridge']
                    ]
                  ],
                  ['match', ['get', 'class'], ['tertiary', 'tertiary_link', 'street', 'street_limited'], true, false],
                  ['!=', ['get', 'oneway'], 'true'],
                  ['!=', ['get', 'oneway'], true],
                  ['!=', ['get', 'oneway'], 1]
                ]}
                layout={{ 'line-cap': 'butt', 'line-join': 'round' }}
                paint={{
                  'line-color': '#FFFFFF',
                  'line-width': ['interpolate', ['linear'], ['zoom'],
                    13, 1, 14, 1.5, 15, 2, 16, 2.5, 18, 3, 20, 4
                  ],
                  'line-dasharray': [3, 3],
                  'line-opacity': ['interpolate', ['linear'], ['zoom'],
                    13, 0.5, 14, 0.8, 15, 1
                  ],
                }}
              />
              {/* Street/Tertiary Lane Dividers - for multi-lane ONEWAY roads only */}
              {/* Renders before secondary surface so it gets masked at intersections with major roads */}
              <Layer
                id="tgs-street-lane-divider"
                type="line"
                source="composite"
                source-layer="road"
                beforeId="tgs-secondary-surface"
                minzoom={14}
                filter={['all',
                  ['any',
                    ['!', ['has', 'structure']],
                    ['==', ['get', 'structure'], ''],
                    ['==', ['get', 'structure'], 'none'],
                    ['all',
                      ['!=', ['get', 'structure'], 'tunnel'],
                      ['!=', ['get', 'structure'], 'bridge']
                    ]
                  ],
                  ['match', ['get', 'class'], ['tertiary', 'tertiary_link', 'street', 'street_limited'], true, false],
                  ['>=', ['coalesce', ['get', 'lanes'], 2], 2],
                  ['any',
                    ['==', ['get', 'oneway'], 'true'],
                    ['==', ['get', 'oneway'], true],
                    ['==', ['get', 'oneway'], 1]
                  ]
                ]}
                layout={{ 'line-cap': 'butt', 'line-join': 'round' }}
                paint={{
                  'line-color': '#FFFFFF',
                  'line-width': ['interpolate', ['linear'], ['zoom'],
                    14, 1, 15, 1.5, 16, 2, 18, 2.5, 20, 3.5
                  ],
                  'line-dasharray': [2, 2],
                  'line-opacity': ['interpolate', ['linear'], ['zoom'],
                    14, 0.5, 15, 0.8, 16, 0.9
                  ],
                }}
              />

              {/* ============================================================ */}
              {/* LAYER 2: SECONDARY - Masks tertiary casing + centerlines     */}
              {/* ============================================================ */}
              {/* Secondary Casing */}
              <Layer
                id="tgs-secondary-casing"
                type="line"
                source="composite"
                source-layer="road"
                beforeId="tgs-surfaces-base"
                filter={['all',
                  ['any',
                    ['!', ['has', 'structure']],
                    ['==', ['get', 'structure'], ''],
                    ['==', ['get', 'structure'], 'none'],
                    ['all',
                      ['!=', ['get', 'structure'], 'tunnel'],
                      ['!=', ['get', 'structure'], 'bridge']
                    ]
                  ],
                  ['match', ['get', 'class'], ['secondary', 'secondary_link'], true, false]
                ]}
                layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                paint={{
                  'line-color': '#D0D0D0',
                  'line-width': ['interpolate', ['linear'], ['zoom'],
                    12, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 2], 4.8],
                    14, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 2], 8.6],
                    16, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 2], 14.6],
                    18, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 2], 26.5],
                    20, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 2], 54]
                  ],
                  'line-opacity': 1,
                }}
              />
              {/* Secondary Surface */}
              <Layer
                id="tgs-secondary-surface"
                type="line"
                source="composite"
                source-layer="road"
                beforeId="tgs-markings-base"
                filter={['all',
                  ['any',
                    ['!', ['has', 'structure']],
                    ['==', ['get', 'structure'], ''],
                    ['==', ['get', 'structure'], 'none'],
                    ['all',
                      ['!=', ['get', 'structure'], 'tunnel'],
                      ['!=', ['get', 'structure'], 'bridge']
                    ]
                  ],
                  ['match', ['get', 'class'], ['secondary', 'secondary_link'], true, false]
                ]}
                layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                paint={{
                  'line-color': '#707070',
                  'line-width': ['interpolate', ['linear'], ['zoom'],
                    12, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 2], 4.2],
                    14, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 2], 7.4],
                    16, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 2], 12.6],
                    18, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 2], 23],
                    20, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 2], 47]
                  ],
                  'line-opacity': 1,
                }}
              />
              {/* Secondary Centerline */}
              {/* Renders before primary surface so it gets masked at intersections with major roads */}
              <Layer
                id="tgs-secondary-centerline"
                type="line"
                source="composite"
                source-layer="road"
                beforeId="tgs-primary-surface"
                minzoom={13}
                filter={['all',
                  ['any',
                    ['!', ['has', 'structure']],
                    ['==', ['get', 'structure'], ''],
                    ['==', ['get', 'structure'], 'none'],
                    ['all',
                      ['!=', ['get', 'structure'], 'tunnel'],
                      ['!=', ['get', 'structure'], 'bridge']
                    ]
                  ],
                  ['match', ['get', 'class'], ['secondary', 'secondary_link'], true, false],
                  ['!=', ['get', 'oneway'], 'true'],
                  ['!=', ['get', 'oneway'], true],
                  ['!=', ['get', 'oneway'], 1]
                ]}
                layout={{ 'line-cap': 'butt', 'line-join': 'round' }}
                paint={{
                  'line-color': '#FFFFFF',
                  'line-width': ['interpolate', ['linear'], ['zoom'],
                    13, 1, 14, 1.5, 15, 2, 16, 2.5, 18, 3, 20, 4
                  ],
                  'line-dasharray': [3, 3],
                  'line-opacity': ['interpolate', ['linear'], ['zoom'],
                    13, 0.5, 14, 0.8, 15, 1
                  ],
                }}
              />
              {/* Secondary Lane Dividers - for multi-lane ONEWAY roads only */}
              {/* Renders before primary surface so it gets masked at intersections with major roads */}
              <Layer
                id="tgs-secondary-lane-divider"
                type="line"
                source="composite"
                source-layer="road"
                beforeId="tgs-primary-surface"
                minzoom={13}
                filter={['all',
                  ['any',
                    ['!', ['has', 'structure']],
                    ['==', ['get', 'structure'], ''],
                    ['==', ['get', 'structure'], 'none'],
                    ['all',
                      ['!=', ['get', 'structure'], 'tunnel'],
                      ['!=', ['get', 'structure'], 'bridge']
                    ]
                  ],
                  ['match', ['get', 'class'], ['secondary', 'secondary_link'], true, false],
                  ['>=', ['coalesce', ['get', 'lanes'], 2], 2],
                  ['any',
                    ['==', ['get', 'oneway'], 'true'],
                    ['==', ['get', 'oneway'], true],
                    ['==', ['get', 'oneway'], 1]
                  ]
                ]}
                layout={{ 'line-cap': 'butt', 'line-join': 'round' }}
                paint={{
                  'line-color': '#FFFFFF',
                  'line-width': ['interpolate', ['linear'], ['zoom'],
                    13, 0.8, 14, 1.2, 15, 1.5, 16, 2, 18, 2.5, 20, 3.5
                  ],
                  'line-dasharray': [2, 2],
                  'line-opacity': ['interpolate', ['linear'], ['zoom'],
                    13, 0.5, 14, 0.8, 15, 0.9
                  ],
                }}
              />

              {/* ============================================================ */}
              {/* LAYER 3: PRIMARY - Masks secondary casing + centerlines      */}
              {/* ============================================================ */}
              {/* Primary Casing */}
              <Layer
                id="tgs-primary-casing"
                type="line"
                source="composite"
                source-layer="road"
                beforeId="tgs-surfaces-base"
                filter={['all',
                  ['any',
                    ['!', ['has', 'structure']],
                    ['==', ['get', 'structure'], ''],
                    ['==', ['get', 'structure'], 'none'],
                    ['all',
                      ['!=', ['get', 'structure'], 'tunnel'],
                      ['!=', ['get', 'structure'], 'bridge']
                    ]
                  ],
                  ['match', ['get', 'class'], ['primary', 'primary_link'], true, false]
                ]}
                layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                paint={{
                  'line-color': '#D0D0D0',
                  'line-width': ['interpolate', ['linear'], ['zoom'],
                    12, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], ['match', ['get', 'class'], 'primary', 4, 2]], 5],
                    14, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], ['match', ['get', 'class'], 'primary', 4, 2]], 9],
                    16, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], ['match', ['get', 'class'], 'primary', 4, 2]], 15.2],
                    18, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], ['match', ['get', 'class'], 'primary', 4, 2]], 27.5],
                    20, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], ['match', ['get', 'class'], 'primary', 4, 2]], 56]
                  ],
                  'line-opacity': 1,
                }}
              />
              {/* Primary Surface */}
              <Layer
                id="tgs-primary-surface"
                type="line"
                source="composite"
                source-layer="road"
                beforeId="tgs-markings-base"
                filter={['all',
                  ['any',
                    ['!', ['has', 'structure']],
                    ['==', ['get', 'structure'], ''],
                    ['==', ['get', 'structure'], 'none'],
                    ['all',
                      ['!=', ['get', 'structure'], 'tunnel'],
                      ['!=', ['get', 'structure'], 'bridge']
                    ]
                  ],
                  ['match', ['get', 'class'], ['primary', 'primary_link'], true, false]
                ]}
                layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                paint={{
                  'line-color': '#707070',
                  'line-width': ['interpolate', ['linear'], ['zoom'],
                    12, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], ['match', ['get', 'class'], 'primary', 4, 2]], 4.4],
                    14, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], ['match', ['get', 'class'], 'primary', 4, 2]], 7.8],
                    16, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], ['match', ['get', 'class'], 'primary', 4, 2]], 13.2],
                    18, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], ['match', ['get', 'class'], 'primary', 4, 2]], 24],
                    20, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], ['match', ['get', 'class'], 'primary', 4, 2]], 49]
                  ],
                  'line-opacity': 1,
                }}
              />
              {/* Primary Lane Dividers */}
              <Layer
                id="tgs-primary-lane-divider-left"
                type="line"
                source="composite"
                source-layer="road"
                beforeId="road-label"
                minzoom={12}
                filter={['all',
                  ['any',
                    ['!', ['has', 'structure']],
                    ['==', ['get', 'structure'], ''],
                    ['==', ['get', 'structure'], 'none'],
                    ['all',
                      ['!=', ['get', 'structure'], 'tunnel'],
                      ['!=', ['get', 'structure'], 'bridge']
                    ]
                  ],
                  ['match', ['get', 'class'], ['primary', 'primary_link'], true, false]
                ]}
                layout={{ 'line-cap': 'butt', 'line-join': 'round' }}
                paint={{
                  'line-color': '#FFFFFF',
                  'line-width': ['interpolate', ['linear'], ['zoom'],
                    12, 0.8, 13, 1, 14, 1.5, 15, 2, 16, 2.5, 18, 3, 20, 4
                  ],
                  'line-offset': ['interpolate', ['linear'], ['zoom'],
                    12, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 4], -0.6],
                    14, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 4], -1.1],
                    16, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 4], -1.8],
                    18, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 4], -3.4],
                    20, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 4], -6.9]
                  ],
                  'line-dasharray': [2, 2],
                  'line-opacity': ['interpolate', ['linear'], ['zoom'],
                    12, 0.5, 13, 0.7, 14, 0.9
                  ],
                }}
              />
              <Layer
                id="tgs-primary-lane-divider-right"
                type="line"
                source="composite"
                source-layer="road"
                beforeId="road-label"
                minzoom={12}
                filter={['all',
                  ['any',
                    ['!', ['has', 'structure']],
                    ['==', ['get', 'structure'], ''],
                    ['==', ['get', 'structure'], 'none'],
                    ['all',
                      ['!=', ['get', 'structure'], 'tunnel'],
                      ['!=', ['get', 'structure'], 'bridge']
                    ]
                  ],
                  ['match', ['get', 'class'], ['primary', 'primary_link'], true, false]
                ]}
                layout={{ 'line-cap': 'butt', 'line-join': 'round' }}
                paint={{
                  'line-color': '#FFFFFF',
                  'line-width': ['interpolate', ['linear'], ['zoom'],
                    12, 0.8, 13, 1, 14, 1.5, 15, 2, 16, 2.5, 18, 3, 20, 4
                  ],
                  'line-offset': ['interpolate', ['linear'], ['zoom'],
                    12, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 4], 0.6],
                    14, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 4], 1.1],
                    16, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 4], 1.8],
                    18, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 4], 3.4],
                    20, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 4], 6.9]
                  ],
                  'line-dasharray': [2, 2],
                  'line-opacity': ['interpolate', ['linear'], ['zoom'],
                    12, 0.5, 13, 0.7, 14, 0.9
                  ],
                }}
              />

              {/* ============================================================ */}
              {/* LAYER 4: TRUNK - Masks primary casing + markings             */}
              {/* ============================================================ */}
              {/* Trunk Casing */}
              <Layer
                id="tgs-trunk-casing"
                type="line"
                source="composite"
                source-layer="road"
                beforeId="tgs-surfaces-base"
                filter={['all',
                  ['any',
                    ['!', ['has', 'structure']],
                    ['==', ['get', 'structure'], ''],
                    ['==', ['get', 'structure'], 'none'],
                    ['all',
                      ['!=', ['get', 'structure'], 'tunnel'],
                      ['!=', ['get', 'structure'], 'bridge']
                    ]
                  ],
                  ['match', ['get', 'class'], ['trunk', 'trunk_link'], true, false]
                ]}
                layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                paint={{
                  'line-color': '#D0D0D0',
                  'line-width': ['interpolate', ['linear'], ['zoom'],
                    12, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], ['match', ['get', 'class'], 'trunk', 4, 2]], 5.2],
                    14, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], ['match', ['get', 'class'], 'trunk', 4, 2]], 9.4],
                    16, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], ['match', ['get', 'class'], 'trunk', 4, 2]], 15.8],
                    18, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], ['match', ['get', 'class'], 'trunk', 4, 2]], 28.5],
                    20, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], ['match', ['get', 'class'], 'trunk', 4, 2]], 58]
                  ],
                  'line-opacity': 1,
                }}
              />
              {/* Trunk Surface */}
              <Layer
                id="tgs-trunk-surface"
                type="line"
                source="composite"
                source-layer="road"
                beforeId="tgs-markings-base"
                filter={['all',
                  ['any',
                    ['!', ['has', 'structure']],
                    ['==', ['get', 'structure'], ''],
                    ['==', ['get', 'structure'], 'none'],
                    ['all',
                      ['!=', ['get', 'structure'], 'tunnel'],
                      ['!=', ['get', 'structure'], 'bridge']
                    ]
                  ],
                  ['match', ['get', 'class'], ['trunk', 'trunk_link'], true, false]
                ]}
                layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                paint={{
                  'line-color': '#707070',
                  'line-width': ['interpolate', ['linear'], ['zoom'],
                    12, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], ['match', ['get', 'class'], 'trunk', 4, 2]], 4.6],
                    14, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], ['match', ['get', 'class'], 'trunk', 4, 2]], 8.2],
                    16, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], ['match', ['get', 'class'], 'trunk', 4, 2]], 13.8],
                    18, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], ['match', ['get', 'class'], 'trunk', 4, 2]], 25],
                    20, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], ['match', ['get', 'class'], 'trunk', 4, 2]], 51]
                  ],
                  'line-opacity': 1,
                }}
              />
              {/* Trunk Lane Dividers */}
              <Layer
                id="tgs-trunk-lane-divider-left"
                type="line"
                source="composite"
                source-layer="road"
                beforeId="road-label"
                minzoom={11}
                filter={['all',
                  ['any',
                    ['!', ['has', 'structure']],
                    ['==', ['get', 'structure'], ''],
                    ['==', ['get', 'structure'], 'none'],
                    ['all',
                      ['!=', ['get', 'structure'], 'tunnel'],
                      ['!=', ['get', 'structure'], 'bridge']
                    ]
                  ],
                  ['match', ['get', 'class'], ['trunk', 'trunk_link'], true, false]
                ]}
                layout={{ 'line-cap': 'butt', 'line-join': 'round' }}
                paint={{
                  'line-color': '#FFFFFF',
                  'line-width': ['interpolate', ['linear'], ['zoom'],
                    11, 0.6, 12, 0.8, 13, 1.2, 14, 1.5, 15, 2, 16, 2.5, 18, 3, 20, 4
                  ],
                  'line-offset': ['interpolate', ['linear'], ['zoom'],
                    11, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 4], -0.5],
                    13, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 4], -0.8],
                    14, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 4], -1.1],
                    16, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 4], -1.8],
                    18, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 4], -3.4],
                    20, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 4], -6.9]
                  ],
                  'line-dasharray': [2, 2],
                  'line-opacity': ['interpolate', ['linear'], ['zoom'],
                    11, 0.4, 12, 0.6, 13, 0.8, 14, 0.9
                  ],
                }}
              />
              <Layer
                id="tgs-trunk-lane-divider-right"
                type="line"
                source="composite"
                source-layer="road"
                beforeId="road-label"
                minzoom={11}
                filter={['all',
                  ['any',
                    ['!', ['has', 'structure']],
                    ['==', ['get', 'structure'], ''],
                    ['==', ['get', 'structure'], 'none'],
                    ['all',
                      ['!=', ['get', 'structure'], 'tunnel'],
                      ['!=', ['get', 'structure'], 'bridge']
                    ]
                  ],
                  ['match', ['get', 'class'], ['trunk', 'trunk_link'], true, false]
                ]}
                layout={{ 'line-cap': 'butt', 'line-join': 'round' }}
                paint={{
                  'line-color': '#FFFFFF',
                  'line-width': ['interpolate', ['linear'], ['zoom'],
                    11, 0.6, 12, 0.8, 13, 1.2, 14, 1.5, 15, 2, 16, 2.5, 18, 3, 20, 4
                  ],
                  'line-offset': ['interpolate', ['linear'], ['zoom'],
                    11, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 4], 0.5],
                    13, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 4], 0.8],
                    14, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 4], 1.1],
                    16, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 4], 1.8],
                    18, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 4], 3.4],
                    20, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 4], 6.9]
                  ],
                  'line-dasharray': [2, 2],
                  'line-opacity': ['interpolate', ['linear'], ['zoom'],
                    11, 0.4, 12, 0.6, 13, 0.8, 14, 0.9
                  ],
                }}
              />
              {/* Trunk Median */}
              <Layer
                id="tgs-trunk-median"
                type="line"
                source="composite"
                source-layer="road"
                beforeId="road-label"
                minzoom={11}
                filter={['all',
                  ['any',
                    ['!', ['has', 'structure']],
                    ['==', ['get', 'structure'], ''],
                    ['==', ['get', 'structure'], 'none'],
                    ['all',
                      ['!=', ['get', 'structure'], 'tunnel'],
                      ['!=', ['get', 'structure'], 'bridge']
                    ]
                  ],
                  ['==', ['get', 'class'], 'trunk']
                ]}
                layout={{ 'line-cap': 'butt', 'line-join': 'round' }}
                paint={{
                  'line-color': '#FFD700',
                  'line-width': ['interpolate', ['linear'], ['zoom'],
                    11, 1, 12, 1.5, 13, 2, 14, 2.5, 16, 4, 18, 6, 20, 8
                  ],
                  'line-opacity': ['interpolate', ['linear'], ['zoom'],
                    11, 0.5, 12, 0.7, 13, 0.9, 14, 1
                  ],
                }}
              />

              {/* ============================================================ */}
              {/* LAYER 5: MOTORWAY - Highest priority, masks all others       */}
              {/* ============================================================ */}
              {/* Motorway Casing */}
              <Layer
                id="tgs-motorway-casing"
                type="line"
                source="composite"
                source-layer="road"
                beforeId="tgs-surfaces-base"
                filter={['all',
                  ['any',
                    ['!', ['has', 'structure']],
                    ['==', ['get', 'structure'], ''],
                    ['==', ['get', 'structure'], 'none'],
                    ['all',
                      ['!=', ['get', 'structure'], 'tunnel'],
                      ['!=', ['get', 'structure'], 'bridge']
                    ]
                  ],
                  ['match', ['get', 'class'], ['motorway', 'motorway_link'], true, false]
                ]}
                layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                paint={{
                  'line-color': '#D0D0D0',
                  'line-width': ['interpolate', ['linear'], ['zoom'],
                    12, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], ['match', ['get', 'class'], 'motorway', 4, 2]], 5.4],
                    14, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], ['match', ['get', 'class'], 'motorway', 4, 2]], 9.8],
                    16, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], ['match', ['get', 'class'], 'motorway', 4, 2]], 16.4],
                    18, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], ['match', ['get', 'class'], 'motorway', 4, 2]], 29.5],
                    20, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], ['match', ['get', 'class'], 'motorway', 4, 2]], 60]
                  ],
                  'line-opacity': 1,
                }}
              />
              {/* Motorway Surface */}
              <Layer
                id="tgs-motorway-surface"
                type="line"
                source="composite"
                source-layer="road"
                beforeId="tgs-markings-base"
                filter={['all',
                  ['any',
                    ['!', ['has', 'structure']],
                    ['==', ['get', 'structure'], ''],
                    ['==', ['get', 'structure'], 'none'],
                    ['all',
                      ['!=', ['get', 'structure'], 'tunnel'],
                      ['!=', ['get', 'structure'], 'bridge']
                    ]
                  ],
                  ['match', ['get', 'class'], ['motorway', 'motorway_link'], true, false]
                ]}
                layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                paint={{
                  'line-color': '#707070',
                  'line-width': ['interpolate', ['linear'], ['zoom'],
                    12, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], ['match', ['get', 'class'], 'motorway', 4, 2]], 4.8],
                    14, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], ['match', ['get', 'class'], 'motorway', 4, 2]], 8.6],
                    16, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], ['match', ['get', 'class'], 'motorway', 4, 2]], 14.4],
                    18, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], ['match', ['get', 'class'], 'motorway', 4, 2]], 26],
                    20, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], ['match', ['get', 'class'], 'motorway', 4, 2]], 53]
                  ],
                  'line-opacity': 1,
                }}
              />
              {/* Motorway Lane Dividers */}
              <Layer
                id="tgs-motorway-lane-divider-left"
                type="line"
                source="composite"
                source-layer="road"
                beforeId="road-label"
                minzoom={10}
                filter={['all',
                  ['any',
                    ['!', ['has', 'structure']],
                    ['==', ['get', 'structure'], ''],
                    ['==', ['get', 'structure'], 'none'],
                    ['all',
                      ['!=', ['get', 'structure'], 'tunnel'],
                      ['!=', ['get', 'structure'], 'bridge']
                    ]
                  ],
                  ['match', ['get', 'class'], ['motorway', 'motorway_link'], true, false]
                ]}
                layout={{ 'line-cap': 'butt', 'line-join': 'round' }}
                paint={{
                  'line-color': '#FFFFFF',
                  'line-width': ['interpolate', ['linear'], ['zoom'],
                    10, 0.5, 11, 0.7, 12, 1, 13, 1.3, 14, 1.6, 15, 2, 16, 2.5, 18, 3, 20, 4
                  ],
                  'line-offset': ['interpolate', ['linear'], ['zoom'],
                    10, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 4], -0.4],
                    12, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 4], -0.6],
                    13, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 4], -0.8],
                    14, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 4], -1.1],
                    16, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 4], -1.8],
                    18, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 4], -3.4],
                    20, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 4], -6.9]
                  ],
                  'line-dasharray': [2, 2],
                  'line-opacity': ['interpolate', ['linear'], ['zoom'],
                    10, 0.4, 11, 0.6, 12, 0.8, 13, 0.9
                  ],
                }}
              />
              <Layer
                id="tgs-motorway-lane-divider-right"
                type="line"
                source="composite"
                source-layer="road"
                beforeId="road-label"
                minzoom={10}
                filter={['all',
                  ['any',
                    ['!', ['has', 'structure']],
                    ['==', ['get', 'structure'], ''],
                    ['==', ['get', 'structure'], 'none'],
                    ['all',
                      ['!=', ['get', 'structure'], 'tunnel'],
                      ['!=', ['get', 'structure'], 'bridge']
                    ]
                  ],
                  ['match', ['get', 'class'], ['motorway', 'motorway_link'], true, false]
                ]}
                layout={{ 'line-cap': 'butt', 'line-join': 'round' }}
                paint={{
                  'line-color': '#FFFFFF',
                  'line-width': ['interpolate', ['linear'], ['zoom'],
                    10, 0.5, 11, 0.7, 12, 1, 13, 1.3, 14, 1.6, 15, 2, 16, 2.5, 18, 3, 20, 4
                  ],
                  'line-offset': ['interpolate', ['linear'], ['zoom'],
                    10, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 4], 0.4],
                    12, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 4], 0.6],
                    13, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 4], 0.8],
                    14, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 4], 1.1],
                    16, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 4], 1.8],
                    18, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 4], 3.4],
                    20, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'], 4], 6.9]
                  ],
                  'line-dasharray': [2, 2],
                  'line-opacity': ['interpolate', ['linear'], ['zoom'],
                    10, 0.4, 11, 0.6, 12, 0.8, 13, 0.9
                  ],
                }}
              />
              {/* Motorway Median */}
              <Layer
                id="tgs-motorway-median"
                type="line"
                source="composite"
                source-layer="road"
                beforeId="road-label"
                minzoom={10}
                filter={['all',
                  ['any',
                    ['!', ['has', 'structure']],
                    ['==', ['get', 'structure'], ''],
                    ['==', ['get', 'structure'], 'none'],
                    ['all',
                      ['!=', ['get', 'structure'], 'tunnel'],
                      ['!=', ['get', 'structure'], 'bridge']
                    ]
                  ],
                  ['==', ['get', 'class'], 'motorway']
                ]}
                layout={{ 'line-cap': 'butt', 'line-join': 'round' }}
                paint={{
                  'line-color': '#FFD700',
                  'line-width': ['interpolate', ['linear'], ['zoom'],
                    10, 0.8, 11, 1, 12, 1.5, 13, 2, 14, 2.5, 16, 4, 18, 6, 20, 8
                  ],
                  'line-opacity': ['interpolate', ['linear'], ['zoom'],
                    10, 0.5, 11, 0.7, 12, 0.9, 13, 1
                  ],
                }}
              />

              {/* ============================================================ */}
              {/* LAYER 6: BRIDGE ROADS - On top of all regular roads          */}
              {/* Uses casing approach: casing first, then surface on top      */}
              {/* ============================================================ */}
              {/* Bridge Casing (white edge effect) */}
              <Layer
                id="tgs-bridge-casing"
                type="line"
                source="composite"
                source-layer="road"
                beforeId="tgs-surfaces-base"
                filter={['all',
                  ['==', ['get', 'structure'], 'bridge'],
                  ['match', ['get', 'class'],
                    ['motorway', 'motorway_link', 'trunk', 'trunk_link', 'primary', 'primary_link',
                     'secondary', 'secondary_link', 'tertiary', 'tertiary_link',
                     'street', 'street_limited', 'residential', 'service'], true, false
                  ]
                ]}
                layout={{ 'line-cap': 'butt', 'line-join': 'round' }}
                paint={{
                  'line-color': '#D0D0D0',
                  'line-width': ['interpolate', ['linear'], ['zoom'],
                    12, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'],
                      ['match', ['get', 'class'], 'motorway', 4, 'motorway_link', 2, 'trunk', 4, 'trunk_link', 2, 'primary', 4, 'primary_link', 2, 2]], 5.4],
                    14, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'],
                      ['match', ['get', 'class'], 'motorway', 4, 'motorway_link', 2, 'trunk', 4, 'trunk_link', 2, 'primary', 4, 'primary_link', 2, 2]], 9.8],
                    16, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'],
                      ['match', ['get', 'class'], 'motorway', 4, 'motorway_link', 2, 'trunk', 4, 'trunk_link', 2, 'primary', 4, 'primary_link', 2, 2]], 16.4],
                    18, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'],
                      ['match', ['get', 'class'], 'motorway', 4, 'motorway_link', 2, 'trunk', 4, 'trunk_link', 2, 'primary', 4, 'primary_link', 2, 2]], 29.5],
                    20, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'],
                      ['match', ['get', 'class'], 'motorway', 4, 'motorway_link', 2, 'trunk', 4, 'trunk_link', 2, 'primary', 4, 'primary_link', 2, 2]], 60]
                  ],
                  'line-opacity': 1,
                }}
              />
              {/* Bridge Surface - uses class-matched colors to match regular roads */}
              <Layer
                id="tgs-bridge-surface"
                type="line"
                source="composite"
                source-layer="road"
                beforeId="tgs-markings-base"
                filter={['all',
                  ['==', ['get', 'structure'], 'bridge'],
                  ['match', ['get', 'class'],
                    ['motorway', 'motorway_link', 'trunk', 'trunk_link', 'primary', 'primary_link',
                     'secondary', 'secondary_link', 'tertiary', 'tertiary_link',
                     'street', 'street_limited', 'residential', 'service'], true, false
                  ]
                ]}
                layout={{ 'line-cap': 'butt', 'line-join': 'round' }}
                paint={{
                  'line-color': '#707070',
                  'line-width': ['interpolate', ['linear'], ['zoom'],
                    12, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'],
                      ['match', ['get', 'class'], 'motorway', 4, 'motorway_link', 2, 'trunk', 4, 'trunk_link', 2, 'primary', 4, 'primary_link', 2, 2]], 4.8],
                    14, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'],
                      ['match', ['get', 'class'], 'motorway', 4, 'motorway_link', 2, 'trunk', 4, 'trunk_link', 2, 'primary', 4, 'primary_link', 2, 2]], 8.6],
                    16, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'],
                      ['match', ['get', 'class'], 'motorway', 4, 'motorway_link', 2, 'trunk', 4, 'trunk_link', 2, 'primary', 4, 'primary_link', 2, 2]], 14.4],
                    18, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'],
                      ['match', ['get', 'class'], 'motorway', 4, 'motorway_link', 2, 'trunk', 4, 'trunk_link', 2, 'primary', 4, 'primary_link', 2, 2]], 26],
                    20, ['*', ['case', ['has', 'lanes'], ['get', 'lanes'],
                      ['match', ['get', 'class'], 'motorway', 4, 'motorway_link', 2, 'trunk', 4, 'trunk_link', 2, 'primary', 4, 'primary_link', 2, 2]], 53]
                  ],
                  'line-opacity': 1,
                }}
              />

            </>
          )}

          {/* Selected location marker */}
          {selectedLocation && (
            <Marker
              longitude={selectedLocation.lng}
              latitude={selectedLocation.lat}
              anchor="center"
            >
              <div
                style={{
                  width: 20,
                  height: 20,
                  backgroundColor: '#4285F4',
                  border: '2px solid white',
                  borderRadius: '50%',
                }}
              />
            </Marker>
          )}


          {/* Workzone polygons */}
          {workzoneGeoJSON.features.length > 0 && (
            <Source id="workzones" type="geojson" data={workzoneGeoJSON}>
              <Layer
                id="workzone-fill"
                type="fill"
                paint={{
                  'fill-color': '#00E676',
                  'fill-opacity': 0.15,
                }}
              />
              <Layer
                id="workzone-line"
                type="line"
                paint={{
                  'line-color': '#00E676',
                  'line-width': 3,
                  'line-opacity': 0.8,
                }}
              />
            </Source>
          )}

          {/* Drawing preview - polygon */}
          {drawingPreviewGeoJSON && (
            <Source id="drawing-preview" type="geojson" data={drawingPreviewGeoJSON}>
              <Layer
                id="drawing-preview-line"
                type="line"
                paint={{
                  'line-color': '#00FF00',
                  'line-width': 2,
                  'line-dasharray': [2, 2],
                }}
              />
            </Source>
          )}
          
          {/* Drawing points markers */}
          {drawingPoints.map((point, idx) => (
            <Marker
              key={`drawing-point-${idx}`}
              longitude={point.lng}
              latitude={point.lat}
              anchor="center"
            >
              <div
                style={{
                  width: 10,
                  height: 10,
                  backgroundColor: '#00FF00',
                  border: '2px solid white',
                  borderRadius: '50%',
                }}
              />
            </Marker>
          ))}

          {/* Arrows - Mapbox line + arrowhead icons (Google Maps equivalent) */}
          {arrows.length > 0 && (
            <>
              <Source id="arrow-lines" type="geojson" data={arrowLineGeoJSON}>
                <Layer
                  id="arrow-hit"
                  type="line"
                  paint={{
                    'line-color': '#000000',
                    'line-width': 20,
                    'line-opacity': 0.001,
                  }}
                />
                <Layer
                  id="arrow-lines"
                  type="line"
                  paint={{
                    'line-color': '#000000',
                    'line-width': 2,
                    'line-opacity': 0.7,
                  }}
                />
              </Source>
              <Source id="arrow-heads" type="geojson" data={arrowHeadGeoJSON}>
                <Layer
                  id="arrow-heads"
                  type="symbol"
                  layout={{
                    'icon-image': 'arrowhead-icon',
                    'icon-size': 1.4,
                    'icon-rotate': ['get', 'bearing'],
                    'icon-rotation-alignment': 'map',
                    'icon-allow-overlap': true,
                    'icon-ignore-placement': true,
                  }}
                />
              </Source>
              <Source id="arrow-bars" type="geojson" data={arrowBarGeoJSON}>
                <Layer
                  id="arrow-bars"
                  type="symbol"
                  layout={{
                    'icon-image': 'arrowbar-icon',
                    'icon-size': 1.1,
                    'icon-rotate': ['get', 'bearing'],
                    'icon-rotation-alignment': 'map',
                    'icon-allow-overlap': true,
                    'icon-ignore-placement': true,
                  }}
                />
              </Source>
            </>
          )}
          {/* Arrow labels */}
          {arrowLabelGeoJSON.features.length > 0 && (
            <Source id="arrow-labels" type="geojson" data={arrowLabelGeoJSON}>
              <Layer
                id="arrow-labels"
                type="symbol"
                layout={{
                  'text-field': ['get', 'label'],
                  'text-size': 12,
                  'text-anchor': 'center',
                  'text-allow-overlap': true,
                  'text-ignore-placement': true,
                }}
                paint={{
                  'text-color': '#111827',
                  'text-halo-color': '#ffffff',
                  'text-halo-width': 2,
                }}
              />
            </Source>
          )}

          {/* Temp arrow preview */}
          {tempArrowGeoJSON && (
            <Source id="temp-arrow" type="geojson" data={tempArrowGeoJSON}>
              <Layer
                id="temp-arrow-line"
                type="line"
                paint={{
                  'line-color': '#000000',
                  'line-width': 2,
                  'line-opacity': 0.7,
                }}
              />
            </Source>
          )}

          {/* Temp arrow label */}
          {arrowDraft?.start && tempArrowEnd && (
            <Marker
              longitude={(arrowDraft.start.lng + tempArrowEnd.lng) / 2}
              latitude={(arrowDraft.start.lat + tempArrowEnd.lat) / 2}
              anchor="center"
            >
              <div
                style={{
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  padding: '2px 6px',
                  borderRadius: 4,
                  fontSize: '14px',
                  fontWeight: 'bold',
                  color: '#000000',
                }}
              >
                {formatDistance(haversineDistance(
                  arrowDraft.start.lat,
                  arrowDraft.start.lng,
                  tempArrowEnd.lat,
                  tempArrowEnd.lng
                ))} m
              </div>
            </Marker>
          )}

          {/* Delineator lines (paths) */}
          {delineatorLinesGeoJSON.features.length > 0 && (
            <Source id="delineator-lines" type="geojson" data={delineatorLinesGeoJSON}>
              <Layer
                id="delineator-lines-stroke"
                type="line"
                paint={{
                  'line-color': [
                    'match',
                    ['get', 'deviceType'],
                    'yellow_dot', '#FF4500',
                    '#F97316' // default orange for cones
                  ],
                  'line-width': 2,
                  'line-opacity': 0.6,
                  'line-dasharray': [4, 4],
                }}
              />
            </Source>
          )}

          {/* Delineator device markers (cones) */}
          {delineatorDevicePositions.map((device, idx) => {
            const size = delineatorBaseSize * device.deviceSize;
            const isDragging = draggingDelineator?.lineId === device.lineId;
            const isDelineatorSelected = selectedElement?.type === 'delineator' && selectedElement?.id === device.lineId;
            
            // Find the full delineator line data for selection
            const delineatorLine = delineatorLines.find(line => line.id === device.lineId);
            
            return (
              <Marker
                key={`delineator-device-${device.lineId}-${idx}`}
                longitude={device.lng}
                latitude={device.lat}
                anchor="center"
                draggable={isManualMode && device.isFirst && currentTool !== 'delineator'}
                onDragStart={() => {
                  if (device.isFirst) {
                    setDraggingDelineator({
                      lineId: device.lineId,
                      startLat: device.lat,
                      startLng: device.lng,
                      originalPath: device.originalPath,
                    });
                  }
                }}
                onDragEnd={(e: MarkerDragEvent) => {
                  if (device.isFirst) {
                    handleDelineatorDragEnd(device.lineId, device.originalPath, e);
                  }
                }}
              >
                <div
                  style={{
                    position: 'relative',
                    width: size,
                    height: size,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: device.isFirst && isManualMode && currentTool !== 'delineator' ? 'move' : 'pointer',
                    opacity: isDragging ? 0.6 : 1,
                    transition: 'opacity 0.15s ease',
                    outline: isDelineatorSelected ? '2px solid #3B82F6' : 'none',
                    outlineOffset: 2,
                    borderRadius: '50%',
                  }}
                  title={device.isFirst ? 'Drag to move entire line' : 'Click to select'}
                  onMouseDown={(e) => {
                    // Prevent event from bubbling to map (which would clear selection)
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    // Prevent event from bubbling to map
                    e.stopPropagation();
                    // Select delineator on click
                    if (delineatorLine) {
                      onElementSelect?.({ type: 'delineator', id: device.lineId, data: delineatorLine });
                    }
                  }}
                >
                  {/* Device SVG - Cone or Traffic Dot based on type */}
                  {device.deviceType === 'yellow_dot' ? (
                    <svg 
                      width={size} 
                      height={size} 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ display: 'block' }}
                    >
                      <circle cx="12" cy="12" r="9" fill="#E6A224"/>
                      <circle cx="12" cy="12" r="6.5" fill="#FF4500"/>
                    </svg>
                  ) : (
                    <svg 
                      width={size} 
                      height={size} 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ display: 'block' }}
                    >
                      <path d="M12 2L6 20H18L12 2Z" fill="#F97316" stroke="#EA580C" strokeWidth="1"/>
                      <rect x="4" y="20" width="16" height="2" rx="0.5" fill="#EA580C"/>
                      <path d="M8 12H16" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                      <path d="M7 16H17" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                  )}
                  {/* Visual indicator for drag handle on first device */}
                  {device.isFirst && isManualMode && currentTool !== 'delineator' && (
                    <div
                      style={{
                        position: 'absolute',
                        top: -4,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: '#3B82F6',
                        border: '2px solid white',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                      }}
                    />
                  )}
                </div>
              </Marker>
            );
          })}

          {/* Delineator draft preview line */}
          {delineatorDraftGeoJSON && (
            <Source id="delineator-draft" type="geojson" data={delineatorDraftGeoJSON}>
              <Layer
                id="delineator-draft-line"
                type="line"
                paint={{
                  'line-color': delineatorDraft?.deviceType === 'yellow_dot' ? '#FF4500' : '#F97316',
                  'line-width': 3,
                  'line-opacity': 0.8,
                  'line-dasharray': [2, 2],
                }}
              />
            </Source>
          )}

          {/* Delineator draft path points */}
          {delineatorDraft?.path.map((point, idx) => (
            <Marker
              key={`delineator-draft-point-${idx}`}
              longitude={point.lng}
              latitude={point.lat}
              anchor="center"
            >
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: delineatorDraft?.deviceType === 'yellow_dot' ? '#FF4500' : '#F97316',
                  border: '2px solid white',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                }}
              />
            </Marker>
          ))}

          {/* Delineator draft device preview */}
          {delineatorDraftPositions.map((pos, idx) => (
            <Marker
              key={`delineator-draft-device-${idx}`}
              longitude={pos.lng}
              latitude={pos.lat}
              anchor="center"
            >
              <div
                style={{
                  width: 14,
                  height: 14,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0.6,
                }}
              >
                {delineatorDraft?.deviceType === 'yellow_dot' ? (
                  <svg 
                    width={14}
                    height={14}
                    viewBox="0 0 24 24" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ display: 'block' }}
                  >
                    <circle cx="12" cy="12" r="9" fill="#E6A224"/>
                    <circle cx="12" cy="12" r="6.5" fill="#FF4500"/>
                  </svg>
                ) : (
                  <svg 
                    width={14}
                    height={14}
                    viewBox="0 0 24 24" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ display: 'block' }}
                  >
                    <path d="M12 2L6 20H18L12 2Z" fill="#F97316" stroke="#EA580C" strokeWidth="1"/>
                    <rect x="4" y="20" width="16" height="2" rx="0.5" fill="#EA580C"/>
                    <path d="M8 12H16" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                    <path d="M7 16H17" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                )}
              </div>
            </Marker>
          ))}

          {/* Road tool overlay */}
          {roadToolGeoJSON && (
            <Source id="road-tool" type="geojson" data={roadToolGeoJSON}>
              <Layer
                id="road-tool-line"
                type="line"
                paint={{
                  'line-color': '#1976d2',
                  'line-width': 3,
                }}
              />
            </Source>
          )}

          {/* Sign markers with leader line callout pattern */}
          {signPlacement?.signs?.map((sign: any) => {
            const rotation = sign.rotation !== undefined ? sign.rotation : 0;
            const isComposite = sign.sign?.code_sa === 'COMPOSITE' && sign.composite;
            // Use composite icon function for composites, regular function for normal signs
            // Generate icon WITHOUT rotation baked in - we'll apply CSS transform for smoother rotation
            const iconUrl = isComposite 
              ? getCompositeSignIconUrl(sign.composite, sign.scale || 1.0, 0)
              : getSignIconUrl(sign.sign?.code_sa, sign.scale || 1.0, 0);
            
            // Fixed pixel size for signs (doesn't change with zoom)
            const baseSize = isComposite ? 48 : 32;
            const size = baseSize * (sign.scale || 1.0);
            
            // Calculate default leader line offset based on sign's lane/position
            const lane = sign.position?.lane?.toLowerCase() || '';
            const isLeftSide = lane.includes('left') || lane.includes('l1') || lane.includes('l2');
            const isOverhead = lane.includes('overhead') || lane.includes('gantry');
            
            // Default offset calculation (used if no custom calloutOffset is set)
            const leaderLength = 60;
            const defaultAngle = isOverhead ? -90 : (isLeftSide ? -135 : -45);
            const defaultRadians = (defaultAngle * Math.PI) / 180;
            const defaultOffsetX = Math.cos(defaultRadians) * leaderLength;
            const defaultOffsetY = Math.sin(defaultRadians) * leaderLength;
            
            // Use custom callout offset if set, otherwise use default
            // If this sign is being dragged, use the live dragging offset
            const isDraggingThis = draggingCallout?.signId === sign.id;
            const offsetX = isDraggingThis && draggingCallout
              ? draggingCallout.currentOffsetX 
              : (sign.calloutOffset?.x ?? defaultOffsetX);
            const offsetY = isDraggingThis && draggingCallout
              ? draggingCallout.currentOffsetY 
              : (sign.calloutOffset?.y ?? defaultOffsetY);
            
            if (!sign?.id || !sign?.sign?.code_sa) return null;
            
            const isSelected = selectedElement?.type === 'sign' && selectedElement?.id === sign.id;
            
            return (
              <Marker
                key={sign.id}
                longitude={sign.lng}
                latitude={sign.lat}
                anchor="center"
                draggable={isManualMode && !draggingCallout}
                onDragEnd={(e: MarkerDragEvent) => handleSignDragEnd(sign.id, e)}
              >
                {/* Container for dot + leader line + sign callout */}
                <div 
                  style={{ position: 'relative', width: 0, height: 0 }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // Clear any dragging state
                    setDraggingCallout(null);
                    // Select sign to open in properties panel
                    onElementSelect?.({ type: 'sign', id: sign.id, data: sign });
                  }}
                >
                  {/* Placement dot (actual sign location on road) */}
                  <div
                    style={{
                      position: 'absolute',
                      left: -4,
                      top: -4,
                      width: 8,
                      height: 8,
                      backgroundColor: 'transparent',
                      border: `1px solid ${isSelected ? '#f44336' : '#000000'}`,
                      borderRadius: '50%',
                      zIndex: 2,
                      cursor: isManualMode ? 'grab' : 'pointer',
                    }}
                    title="Drag dot to move sign"
                  />
                  
                  {/* Leader line (inline SVG for smooth rendering) */}
                  <svg
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      overflow: 'visible',
                      pointerEvents: 'none',
                      zIndex: 1,
                    }}
                    width="1"
                    height="1"
                  >
                    <line
                      x1="0"
                      y1="0"
                      x2={offsetX}
                      y2={offsetY}
                      stroke={isSelected ? '#f44336' : '#000000'}
                      strokeWidth="1"
                    />
                  </svg>
                  
                  {/* Sign callout (offset from dot) - drag to reposition label */}
                  <div
                    style={{
                      position: 'absolute',
                      left: offsetX - size / 2,
                      top: offsetY - size / 2,
                      cursor: isManualMode ? (isDraggingThis ? 'grabbing' : 'grab') : 'pointer',
                      zIndex: 3,
                    }}
                    title="Drag sign to reposition label"
                    onMouseDown={(e) => {
                      e.stopPropagation(); // Prevent Marker drag when dragging callout
                      if (e.button === 0) { // Left click only
                        // Always select the sign when clicking on it
                        onElementSelect?.({ type: 'sign', id: sign.id, data: sign });
                        
                        // Only start drag if in manual mode
                        if (isManualMode) {
                          handleCalloutDragStart(
                            sign.id,
                            sign.calloutOffset?.x ?? defaultOffsetX,
                            sign.calloutOffset?.y ?? defaultOffsetY,
                            e
                          );
                        }
                      }
                    }}
                  >
                    {iconUrl ? (
                      <img
                        src={iconUrl}
                        alt={sign.sign.code_sa}
                        draggable={false}
                        style={{
                          width: size,
                          height: size,
                          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                          pointerEvents: 'none',
                          border: isSelected ? '2px solid #f44336' : 'none',
                          borderRadius: 4,
                          transform: rotation !== 0 ? `rotate(${rotation}deg)` : undefined,
                          transition: 'transform 0.1s ease-out',
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: size,
                          height: size,
                          backgroundColor: isComposite ? '#9C27B0' : '#FFD700',
                          border: isSelected ? '2px solid #f44336' : '2px solid #000',
                          borderRadius: '4px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                          pointerEvents: 'none',
                          transform: rotation !== 0 ? `rotate(${rotation}deg)` : undefined,
                          transition: 'transform 0.1s ease-out',
                        }}
                      />
                    )}
                  </div>
                </div>
              </Marker>
            );
          })}

          {/* Cone markers */}
          {cones?.map((cone: any) => {
            return cone?.id && Number.isFinite(cone.lat) && Number.isFinite(cone.lng) ? (
              <Marker
                key={cone.id}
                longitude={cone.lng}
                latitude={cone.lat}
                anchor="center"
              >
                <div
                  style={{
                    width: 16,
                    height: 16,
                    backgroundColor: '#FF6B35',
                    border: '2px solid #D84315',
                    borderRadius: '50%',
                  }}
                  title="Traffic Cone"
                />
              </Marker>
            ) : null;
          })}

          </Map>
      </Box>

    </Paper>
  );
};
